/**
 * ItemDetailUnified - Pagina dettaglio completamente unificata per Barche e Motori
 * 
 * Include:
 * - Galleria immagini
 * - Prenotazioni (ReservationBadge, ReservationButton)
 * - Trattative (Trade modal)
 * - Link temporanei (Share)
 * - Storico prezzi
 * - Storico modifiche
 * - Condivisione WhatsApp
 * 
 * Usa itemConfig.ts per personalizzare campi e comportamento.
 */

 import { useState, useMemo, useEffect, useRef } from 'react';
 import { useParams, useNavigate } from 'react-router-dom';
 import { useQuery, useMutation } from 'react-query';
 import toast from 'react-hot-toast';
import api from '../utils/api';
import { motorApi } from '../utils/api';
import { fixImageUrl } from '../utils/images';
import { useUser, useIsAuthenticated, useCanEditItem, useCanDeleteItem, useCanReserve, useCanCancelReservation } from '../stores/auth';
 import { QuoteRequestButton } from '../components/QuoteRequestButton';
 import { trackPublicView, trackPublicClick, trackPublicQuoteRequest, hasCookieConsent } from '../utils/consentTracking';
 import { getItemConfig, ItemType, DEFAULT_COORDS } from '../config/itemConfig';
 import { useTracking } from '../hooks/useTrackingStats';
 import IOSheet from '../components/IOSheet';
 import { ReservationBadge } from '../components/ReservationBadge';
 import DesktopItemGallery from '../components/DesktopItemGallery';
 import { useDeviceMode } from '../hooks/useDeviceMode';
 import { PriceDisplay, ConditionBadge, SpecCard, TechSpecsGrid, DesktopDetailLayout, MobileDetailTabs } from '../components/Detail';
 import { BoatTypeBadge, BOAT_TYPES } from '../components/BoatTypeBadge';
import { ItemDescription } from '../components/ItemDescription';
import MarketPrice from '../components/MarketPrice';
  import {
    ArrowLeftLucideIcon,
    PhoneLucideIcon,
    MessageCircleLucideIcon,
    MailLucideIcon,
    MapPinLucideIcon,
    CalendarLucideIcon,
    RulerLucideIcon,
    ShareLucideIcon,
    MessageSquareLucideIcon,
    NavigationLucideIcon,
    AnchorLucideIcon,
    SettingsLucideIcon,
    ZapLucideIcon,
    ClockLucideIcon,
    ChevronLeftIcon,
    ChevronRightLucideIcon,
    XIcon,
    CloseIcon,
    EditLucideIcon,
    TrashLucideIcon,
    ZoomInLucideIcon,
    PlayLucideIcon,
    CheckCircleLucideIcon,
    EyeLucideIcon,
    EyeOffLucideIcon,
    ChevronDownIcon,
    ChevronUpIcon,
    WavesLucideIcon,
    BatteryLucideIcon,
    TvLucideIcon,
    ShieldLucideIcon,
    FileLucideIcon,
    CompassLucideIcon,
    FuelLucideIcon,
    GaugeLucideIcon,
    WeightLucideIcon,
    HistoryLucideIcon,
    ShipIcon,
    StarIcon,
    TagLucideIcon,
    SunLucideIcon,
    RadioLucideIcon,
    ArmchairLucideIcon,
    DollarSignLucideIcon,
    StarLucideIcon,
    HomeLucideIcon,
    PackageLucideIcon,
    DownloadLucideIcon
  } from '../components/Icons';
import { generateItemPrint } from '../utils/printGenerator';
import { usePageTitle } from '../contexts/PageTitleContext';
import { SEOFaqSection } from '../components/SEO/SEOFaqSection';

// Boat/Motor/Accessory FAQ data
const boatFAQs = [
  {
    question: "Posso provare questa barca prima dell'acquisto?",
    answer: "Sì, contatta il dealer e richiedi un appuntamento per il test in mare. La maggior parte dei dealer offre prove gratuite (1-2 ore) per barche sopra €50k. Per barche di valore inferiore, potrebbe essere richiesto un deposito cauzionale."
  },
  {
    question: "Quali documenti riceverò con l'acquisto?",
    answer: "Per barche nuove: contratto di compravendita, fattura, certificato del cantiere, scheda di garanzia, fascicolo tecnico. Per barche usate: contratto di compravendita, fattura, atto di provenienza, certificato RINA (se rinnovato)."
  },
  {
    question: "Come viene gestito il trasporto della barca?",
    answer: "Il trasporto può essere organizzato dal dealer o dall'acquirente. Costi indicativi: trasporto su rimorchio €0.5-1/km, trasporto con barca all'interno €2-5/km. Per consegne fuori Italia, contatta il dealer."
  },
  {
    question: "Cosa include la garanzia?",
    answer: "Le barche nuove hanno garanzia del cantiere (tipicamente 2 anni scafo, 1 anno motori). Le barche 'Certificato' hanno estensione garanzia del cantiere 6-12 mesi. La garanzia copre difetti di fabbricazione, non danni da utilizzo."
  }
];

const motorFAQs = [
  {
    question: "Il motore include garanzia?",
    answer: "Sì, i motori nuovi includono garanzia del produttore (tipicamente 2 anni). I motori usati certificati hanno garanzia estesa del rivenditore 6-12 mesi."
  },
  {
    question: "È possibile installare il motore da voi?",
    answer: "Sì, collaboriamo con centri assistenza autorizzati in tutta Italia. Contatta il dealer per un preventivo di installazione."
  },
  {
    question: "Quali documenti servono per la registrazione?",
    answer: "Per motorinuovi: fattura, certificato di conformità, dichiarazione di potenza. Per motori usati: atto di provenienza, documentazione precedente."
  }
];

const accessoryFAQs = [
  {
    question: "Gli accessori includono garanzia?",
    answer: "Sì, gli accessori nuovi includono garanzia del produttore (tipicamente 1-2 anni). Gli accessori usati certificati hanno garanzia estesa del rivenditore 6-12 mesi."
  },
  {
    question: "Come vengono spediti gli accessori?",
    answer: "Gli accessori vengono spediti tramite corriere espresso. Costi e tempi di consegna variano in base alle dimensioni e al peso. Per accessori voluminosi, contatta il dealer per preventivo di spedizione."
  },
  {
    question: "Posso restituire un accessorio?",
    answer: "Sì, hai 14 giorni di tempo per esercitare il diritto di recesso. L'accessorio deve essere restituito nelle stesse condizioni in cui è stato ricevuto. Contatta il dealer per le istruzioni di reso."
  }
];

// Calcola distanza
const calcDistance = (fromLat: number, fromLng: number, toLat: number, toLng: number): number => {
  const R = 6371;
  const dLat = (toLat - fromLat) * Math.PI / 180;
  const dLng = (toLng - fromLng) * Math.PI / 180;
  const a = Math.sin(dLat / 2) * Math.sin(dLat / 2) +
            Math.cos(fromLat * Math.PI / 180) * Math.cos(toLat * Math.PI / 180) *
            Math.sin(dLng / 2) * Math.sin(dLng / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return Math.round(R * c);
};

// Formatta prezzo
const formatPrice = (price: number | undefined | null, showPrices: boolean = true): string => {
  if (!price || price === 0) return 'Contatta il seller';
  if (!showPrices) return 'Contatta il seller';
  return `€ ${price.toLocaleString('it-IT')}`;
};

// Funzione per ottenere le specifiche tecniche dalla tipologia
function getTechnicalSpecs(specsJson: any, boatTypeCode: string) {
  if (!specsJson) return null;

  const specs =
    typeof specsJson === "string" ? JSON.parse(specsJson) : specsJson;

  const fieldsByType: Record<string, { key: string; label: string }[]> = {
    GOM: [
      { key: "scafo.materiale_tessuto", label: "Materiale Tessuto" },
      { key: "scafo.grammatura_tessuto_gsm", label: "Grammatura (g/m²)" },
      { key: "scafo.compartimenti_tubolari", label: "Compartimenti" },
      { key: "scafo.diametro_tubolare_m", label: "Diametro Tubolare (m)" },
      { key: "scafo.tipo_valvola", label: "Tipo Valvola" },
    ],
    RIB: [
      { key: "scafo.materiale_scafo", label: "Materiale Scafo" },
      { key: "scafo.angolo_deadrise", label: "Angolo Deadrise (°)" },
      { key: "scafo.numero_step", label: "Numero Step" },
      { key: "scafo.materiali_tubolari", label: "Materiali Tubolari" },
    ],
    TEN: [
      { key: "propulsione.richiudibile", label: "Richiudibile" },
      { key: "propulsione.sistema_sgancio", label: "Sistema Sgancio" },
      { key: "propulsione.compatibile_gruette", label: "Compatibile Gruette" },
    ],
    FLY: [
      { key: "dimensioni.cabine", label: "Cabine" },
      { key: "dimensioni.bagni", label: "Bagni" },
      { key: "impianti.generatore_kva", label: "Generatore (kVA)" },
      { key: "impianti.dissalatore_galloni_giorno", label: "Dissalatore" },
    ],
  };

  const fields = fieldsByType[boatTypeCode] || [];
  return fields
    .map((f) => {
      const keys = f.key.split(".");
      let value = specs;
      for (const k of keys) {
        value = value?.[k];
      }
      return { label: f.label, value };
    })
    .filter((f) => f.value !== undefined && f.value !== null);
}

// Calcola la percentuale di guadagno
const calculateProfitPercentage = (dealerPrice?: number, retailPrice?: number): number | null => {
  if (!dealerPrice || !retailPrice || retailPrice <= dealerPrice) return null;
  return Math.round(((retailPrice - dealerPrice) / dealerPrice) * 100);
};

// ===========================================
// COMPONENTE DINAMICO PER SEZIONI OPTIONAL
// ===========================================

// Configurazione categorie con icone e colori
const categoryConfig: Record<string, { icon: React.ReactNode; label: string; color: string }> = {
  motors: { icon: <ZapLucideIcon className="w-3 h-3" />, label: 'Motori', color: 'text-orange-600' },
  fuel: { icon: <FuelLucideIcon className="w-3 h-3" />, label: 'Impianti', color: 'text-stone-600' },
  interior: { icon: <HomeLucideIcon className="w-3 h-3" />, label: 'Interni', color: 'text-blue-600' },
  exterior: { icon: <SunLucideIcon className="w-3 h-3" />, label: 'Esterni', color: 'text-amber-600' },
  electronics: { icon: <RadioLucideIcon className="w-3 h-3" />, label: 'Elettronica', color: 'text-purple-600' },
  navigation: { icon: <CompassLucideIcon className="w-3 h-3" />, label: 'Navigazione', color: 'text-cyan-600' },
  entertainment: { icon: <TvLucideIcon className="w-3 h-3" />, label: 'Intrattenimento', color: 'text-pink-600' },
  comfort: { icon: <ArmchairLucideIcon className="w-3 h-3" />, label: 'Comfort', color: 'text-green-600' },
  safety: { icon: <ShieldLucideIcon className="w-3 h-3" />, label: 'Sicurezza', color: 'text-red-600' },
  mechanical: { icon: <SettingsLucideIcon className="w-3 h-3" />, label: 'Meccanica', color: 'text-gray-600' },
  other: { icon: <StarLucideIcon className="w-3 h-3" />, label: 'Altro', color: 'text-indigo-600' },
};

// Componente per sezioni dinamiche di optional
function DynamicSection({
  category,
  features,
  icon,
  title,
  color,
  showPrices,
  is_new_boat,
}: {
  category: string;
  features: any[];
  icon: React.ReactNode;
  title: string;
  color: string;
  showPrices: boolean;
  is_new_boat?: boolean;
}) {
  const filtered = features.filter((f) => f.category === category);

  if (filtered.length === 0) return null;

  return (
    <div className="glass-card mb-3">
      <h3 className={`font-semibold text-gray-900 mb-2 text-xs flex items-center gap-2 ${color}`}>
        {icon} {title} ({filtered.length})
      </h3>
      <div className="grid grid-cols-2 gap-x-3 gap-y-1 text-[10px]">
        {filtered.map((feature, idx) => (
          <div key={`${feature.name}-${idx}`} className="flex justify-between">
            <span className="text-gray-500">{feature.name}</span>
            <span className="text-gray-900 font-medium">
              {feature.is_premium && <StarIcon className="w-3 h-3 inline-block mr-0.5 fill-current text-yellow-500" />}
              {showPrices && feature.added_price > 0 && `+€${feature.added_price.toLocaleString('it-IT')}`}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}

// ===========================================
// COMPONENTI UI - ACCORDION
// ===========================================

// Componente accordion section
function AccordionSection({ 
  id, 
  title, 
  icon, 
  isOpen, 
  onToggle, 
  children 
}: { 
  id: string; 
  title: string; 
  icon?: React.ReactNode; 
  isOpen: boolean; 
  onToggle: () => void; 
  children: React.ReactNode;
}) {
  return (
    <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
      <button
        type="button"
        onClick={onToggle}
        className="w-full flex items-center justify-between p-3 text-left hover:bg-gray-50 transition-colors"
      >
        <div className="flex items-center gap-2">
          {icon && <span className="text-gray-400">{icon}</span>}
          <span className="text-xs font-semibold text-gray-900">{title}</span>
        </div>
        {isOpen ? (
          <ChevronUpIcon className="w-4 h-4 text-gray-400" />
        ) : (
          <ChevronDownIcon className="w-4 h-4 text-gray-400" />
        )}
      </button>
      {isOpen && (
        <div className="px-3 pb-3">
          {children}
        </div>
      )}
    </div>
  );
}

// Componente per singolo specifica
function SpecItem({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex justify-between py-1.5 border-b border-[var(--ios-separator)]/20">
      <span className="text-[12px] text-[var(--ios-text-tertiary)]">{label}</span>
      <span className="text-[12px] font-medium text-[var(--ios-text-primary)]">{value}</span>
    </div>
  );
}

  // ReservationBadge imported from components
  
  // Componente Pulsante Prenotazione
  function ReservationButton({
    itemId,
    itemType,
    itemStatus,
    reservation,
    currentUserId,
    onReservationChange,
    trackClick,
    item,
    className = ''
  }: {
    itemId: number;
    itemType: ItemType;
    itemStatus: string;
    reservation?: any;
    currentUserId: number;
    onReservationChange: () => void;
    trackClick: (data: any) => Promise<any>;
    item?: any;
    className?: string;
  }) {
    const [isReserving, setIsReserving] = useState(false);
    
    const endpoint = itemType === 'boats' ? 'boats' : itemType === 'motors' ? 'motors' : 'accessories';
    
    const reserveMutation = useMutation(
      () => api.post(`/${endpoint}/${itemId}/reserve`),
      {
        onSuccess: () => {
          // Track reservation click
          trackClick({
            item_type: itemType === 'boats' ? 'boat' : itemType === 'motors' ? 'motor' : 'accessory',
            item_id: itemId as number,
            click_type: 'quote',
            viewer_region: item?.region || '',
            viewer_provincia: item?.provincia || ''
          }).catch(console.error);
          toast.success('Prenotazione effettuata!');
          onReservationChange();
        },
        onError: () => {
          toast.error('Errore nella prenotazione');
          setIsReserving(false);
        }
      }
    );

    const cancelMutation = useMutation(
      () => api.delete(`/${endpoint}/${itemId}/reserve`),
      {
        onSuccess: () => {
          // Track reservation cancellation
          trackClick({
            item_type: itemType === 'boats' ? 'boat' : itemType === 'motors' ? 'motor' : 'accessory',
            item_id: itemId as number,
            click_type: 'quote',
            viewer_region: item?.region || '',
            viewer_provincia: item?.provincia || ''
          }).catch(console.error);
          toast.success('Prenotazione cancellata');
          onReservationChange();
        },
        onError: () => {
          toast.error('Errore nella cancellazione');
        }
      }
    );

  const isMyReservation = reservation?.dealer_id === currentUserId;
  const hasReservation = !!reservation;

  // FIX: Accept both 'available' and 'published' status
  if (itemStatus !== 'available' && itemStatus !== 'published') {
    return null;
  }

  if (hasReservation) {
    if (isMyReservation) {
      return (
        <button
          onClick={() => cancelMutation.mutate()}
          disabled={cancelMutation.isLoading}
          className={`h-9 px-3 rounded-lg bg-yellow-500 text-white text-[12px] font-semibold active:opacity-80 whitespace-nowrap shrink-0 flex items-center gap-1 ${className}`}
        >
          <ClockLucideIcon className="w-3.5 h-3.5" />
          {cancelMutation.isLoading ? 'Cancellazione...' : 'Cancella'}
        </button>
      );
    }
    return (
      <div className="text-[12px] text-gray-500 shrink-0 px-2">
        Già prenotato
      </div>
    );
  }

  return (
    <button
      onClick={() => reserveMutation.mutate()}
      disabled={isReserving}
      data-testid="reserve-button"
      className={`h-9 px-3 rounded-lg bg-[var(--ios-blue)] text-white text-[12px] font-semibold active:opacity-80 whitespace-nowrap shrink-0 flex items-center gap-1 ${className}`}
    >
      <ClockLucideIcon className="w-3.5 h-3.5" />
      {isReserving ? 'Attendi...' : 'Prenota'}
    </button>
  );
}

// Tipo per media (immagini e video)
type MediaItem = {
  type: 'image' | 'video';
  url: string;
  thumbnail?: string;
  videoType?: 'mp4' | 'youtube';
  youtubeId?: string;
};

// Funzione per determinare il tipo di video
function getVideoType(url: string): { type: 'mp4' | 'youtube'; id?: string } {
  if (!url) return { type: 'mp4' };
  
  // YouTube patterns
  const youtubePatterns = [
    /(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/)([^&\s?]+)/,
    /youtube\.com\/watch\?.*v=([^&\s?]+)/,
  ];
  
  for (const pattern of youtubePatterns) {
    const match = url.match(pattern);
    if (match && match[1]) {
      return { type: 'youtube', id: match[1] };
    }
  }
  
  return { type: 'mp4' };
}

// Componente Galleria Modern con thumbnails, zoom e video
function ItemGallery({ images, videos, title, distance, status, reservation, itemData }: {
  images: string[];
  videos?: string[];
  title: string;
  distance?: number | null;
  status?: string;
  reservation?: any;
  itemData?: any;
}) {
  const thumbnailsRef = useRef<HTMLDivElement>(null);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [showLightbox, setShowLightbox] = useState(false);

  const mediaItems: MediaItem[] = useMemo((): MediaItem[] => {
    const items: MediaItem[] = [];
    if (images && images.length > 0) {
      images.forEach((img: string) => items.push({ type: 'image', url: fixImageUrl(img) }));
    }
    if (videos && videos.length > 0) {
      videos.forEach((video: string) => {
        const videoInfo = getVideoType(video);
        items.push({ type: 'video', url: video, videoType: videoInfo.type, youtubeId: videoInfo.id });
      });
    }
    return items;
  }, [images, videos]);

  const currentMedia = mediaItems[currentIndex];
  const totalItems = mediaItems.length;

  const goToPrev = () => setCurrentIndex((prev) => (prev > 0 ? prev - 1 : totalItems - 1));
  const goToNext = () => setCurrentIndex((prev) => (prev < totalItems - 1 ? prev + 1 : 0));

  const handleThumbnailClick = (index: number) => {
    setCurrentIndex(index);
    if (thumbnailsRef.current) {
      const child = thumbnailsRef.current.children[index] as HTMLElement;
      child?.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' });
    }
  };

  const openLightbox = () => {
    if (currentMedia?.type === 'image') setShowLightbox(true);
  };

  // Touch swipe for gallery
  const touchStartX = useRef(0);
  const handleTouchStart = (e: React.TouchEvent) => { touchStartX.current = e.touches[0].clientX; };
  const handleTouchEnd = (e: React.TouchEvent) => {
    const diff = touchStartX.current - e.changedTouches[0].clientX;
    if (Math.abs(diff) > 50) {
      diff > 0 ? goToNext() : goToPrev();
    }
  };

  // Reservation badge for gallery overlay (non-absolute, renderizzato inline)
  const showReservationBadge = itemData?.has_active_reservation && reservation;

  if (!mediaItems || mediaItems.length === 0) {
    return (
      <div className="w-full aspect-[4/3] bg-gray-100 flex items-center justify-center text-gray-400">
        <AnchorLucideIcon className="h-24 w-24" />
      </div>
    );
  }

  return (
    <div className="pwa-gallery-root" data-testid="item-gallery">
      {/* Main Image/Video */}
      <div className="pwa-gallery-main relative bg-black"
        onTouchStart={handleTouchStart}
        onTouchEnd={handleTouchEnd}
      >
        {currentMedia?.type === 'image' ? (
          <img
            src={currentMedia.url}
            alt={`${title} - ${currentIndex + 1}`}
            className="pwa-gallery-img"
            width={800}
            height={600}
            fetchpriority="high"
            onClick={openLightbox}
          />
        ) : currentMedia?.videoType === 'youtube' && currentMedia?.youtubeId ? (
          <iframe
            src={`https://www.youtube.com/embed/${currentMedia.youtubeId}?autoplay=0&rel=0`}
            title="YouTube video"
            className="w-full h-full"
            frameBorder="0"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowFullScreen
          />
        ) : (
          <video src={currentMedia?.url} controls className="w-full h-full object-contain" playsInline />
        )}

        {/* Reservation badge - fuori dal flow assoluto */}
        {showReservationBadge && (
          <div className="absolute top-2 left-2 z-20">
            <ReservationBadge
              expiresAt={reservation.expires_at}
              dealerName={reservation.dealer_name}
              isMyReservation={false}
            />
          </div>
        )}

        {/* Status badge - mostra solo se non c'è reservation */}
        {!showReservationBadge && status && status !== 'available' && status !== 'published' && (
          <div className="absolute top-2 left-2 z-20">
            <span className={`px-2.5 py-0.5 rounded-lg text-[10px] font-bold uppercase ${
              status === 'reserved' ? 'bg-yellow-500 text-white' :
              status === 'sold' ? 'bg-red-500 text-white' :
              'bg-slate-800/80 text-white'
            }`}>
              {status === 'reserved' ? 'Prenotato' :
               status === 'sold' ? 'Venduto' :
               status === 'trade' ? 'In Trattativa' : status}
            </span>
          </div>
        )}

        {/* Video indicator - a destra quando c'è status a sinistra */}
        {currentMedia?.type === 'video' && (
          <div className="absolute top-2 right-2 z-20">
            <span className="bg-red-600 text-white text-[10px] font-bold px-2 py-0.5 rounded-lg flex items-center gap-1">
              <PlayLucideIcon className="w-3 h-3" />
              {currentMedia.videoType === 'youtube' ? 'YouTube' : 'Video'}
            </span>
          </div>
        )}

        {/* Counter badge - sempre visibile se ci sono più media */}
        {totalItems > 1 && (
          <div className="absolute top-2 right-2 z-20 bg-black/50 text-white text-xs px-2 py-0.5 rounded-full backdrop-blur-sm">
            {currentIndex + 1} / {totalItems}
          </div>
        )}

        {/* Distance badge */}
        {distance !== null && distance !== undefined && (
          <div className="absolute bottom-2 left-2 z-20 bg-sky-600/90 text-white text-[10px] font-bold px-2 py-0.5 rounded-lg">
            {distance} km
          </div>
        )}

        {/* Nav arrows */}
        {totalItems > 1 && (
          <>
            <button onClick={goToPrev}
              className="absolute left-2 top-1/2 -translate-y-1/2 w-8 h-8 bg-black/30 backdrop-blur-sm rounded-full flex items-center justify-center text-white active:scale-90 z-20">
              <ChevronLeftIcon className="w-4 h-4" />
            </button>
            <button onClick={goToNext}
              className="absolute right-2 top-1/2 -translate-y-1/2 w-8 h-8 bg-black/30 backdrop-blur-sm rounded-full flex items-center justify-center text-white active:scale-90 z-20">
              <ChevronRightLucideIcon className="w-4 h-4" />
            </button>
          </>
        )}
      </div>

      {/* Thumbnail strip */}
      {totalItems > 1 && (
        <div className="pwa-gallery-thumbs" ref={thumbnailsRef}>
          {mediaItems.map((media, idx) => (
            <button
              key={idx}
              onClick={() => handleThumbnailClick(idx)}
              className={`pwa-gallery-thumb ${idx === currentIndex ? 'active' : ''}`}
            >
              {media.type === 'image' ? (
                <img src={media.url} alt="" width={80} height={60} className="w-full h-full object-cover" />
              ) : (
                <div className="w-full h-full bg-gradient-to-br from-red-600 to-red-800 flex items-center justify-center">
                  <div className="w-5 h-5 rounded-full bg-white/90 flex items-center justify-center">
                    <PlayLucideIcon className="w-2.5 h-2.5 text-red-600 ml-0.5" />
                  </div>
                </div>
              )}
            </button>
          ))}
        </div>
      )}

      {/* Fullscreen Lightbox */}
      {showLightbox && currentMedia?.type === 'image' && (
        <div className="pwa-gallery-fullscreen" onClick={() => setShowLightbox(false)}>
          <button className="pwa-gallery-close" onClick={() => setShowLightbox(false)}>
            <XIcon className="w-6 h-6" />
          </button>
          <img src={currentMedia.url} alt={title} width={800} height={600} className="max-w-full max-h-full object-contain" onClick={(e) => e.stopPropagation()} />
          {totalItems > 1 && (
            <>
              <button onClick={(e) => { e.stopPropagation(); goToPrev(); }}
                className="pwa-gallery-nav left-4">
                <ChevronLeftIcon className="w-8 h-8" />
              </button>
              <button onClick={(e) => { e.stopPropagation(); goToNext(); }}
                className="pwa-gallery-nav right-4">
                <ChevronRightLucideIcon className="w-8 h-8" />
              </button>
            </>
          )}
          <div className="absolute bottom-8 left-0 right-0 text-center text-white/70 text-sm">
            {currentIndex + 1} / {totalItems}
          </div>
        </div>
      )}
    </div>
  );
}

// Componente Principale
interface ItemDetailUnifiedProps {
  itemType?: ItemType;
}

export default function ItemDetailUnified({ itemType: propItemType }: ItemDetailUnifiedProps) {
  const { itemType: paramItemType, id } = useParams<{ itemType: ItemType; id: string }>();
  
  const itemType = propItemType || paramItemType || 'boats';
  const navigate = useNavigate();
  const isAuthenticated = useIsAuthenticated();
  const user = useUser();
  const itemId = parseInt(id || '0');
  
  const config = getItemConfig(itemType || 'boats');
  
  // State per filtri/geolocalizzazione
  const [location, setLocation] = useState<{ lat: number; lng: number } | null>(null);
  const [useFixedLocation, setUseFixedLocation] = useState(true);
  
  // State per modal/trattative
  const [showTradeModal, setShowTradeModal] = useState(false);
  const [tradePrice, setTradePrice] = useState('');
  const [tradeMessage, setTradeMessage] = useState('');
  
  // State per share
  const [showShareModal, setShowShareModal] = useState(false);
  const [shareHours, setShareHours] = useState(24);
  const [generatedLink, setGeneratedLink] = useState('');
  const [shareData, setShareData] = useState<any>(null);
  
  // State per mark-sold
  const [showMarkSoldModal, setShowMarkSoldModal] = useState(false);
  
  // State per nascondere/mostrare prezzi
  const [showPrices, setShowPrices] = useState(true);
  
  // Per utenti pubblici: nascondi sempre prezzo dealer
  const isPublicView = !isAuthenticated;
  
  // Accordion state per le sezioni espandibili
  const [openSections, setOpenSections] = useState<string[]>(['description', 'specs']);
  
  // Badge/Chip state per boats - più intuitivo dei tab
  // NUOVA CONFIGURAZIONE: 4 sezioni invece di 9
  const [activeSection, setActiveSection] = useState<string>('info');
  
  // Nuova configurazione navigazione semplificata (6-7 tab)
  const detailSections = [
    { id: 'info', label: 'Info', icon: FileLucideIcon, color: 'bg-primary-100 text-primary-700 border-primary-300' },
    { id: 'specs', label: 'Scheda', icon: TagLucideIcon, color: 'bg-blue-100 text-blue-700 border-blue-300' },
    ...(itemType === 'boats' ? [{ id: 'motors', label: 'Motori', icon: SettingsLucideIcon, color: 'bg-orange-100 text-orange-700 border-orange-300' }] : []),
    { id: 'tech', label: 'Tech', icon: FuelLucideIcon, color: 'bg-amber-100 text-amber-700 border-amber-300' },
    { id: 'optionals', label: 'Optionals', icon: StarLucideIcon, color: 'bg-purple-100 text-purple-700 border-purple-300' },
    ...(itemType === 'accessories' ? [{ id: 'accessories', label: 'Accessori', icon: PackageLucideIcon, color: 'bg-violet-100 text-violet-700 border-violet-300' }] : []),
    { id: 'techdetails', label: 'Dettagli Tec.', icon: FileLucideIcon, color: 'bg-emerald-100 text-emerald-700 border-emerald-300' },
  ];
  
  // Mobile tab state
  const [activeMobileTab, setActiveMobileTab] = useState<'info' | 'specs' | 'tech' | 'extra' | 'all'>('all');

  // Toggle per accordion
  const toggleSection = (section: string) => {
    setOpenSections(prev => 
      prev.includes(section) 
        ? prev.filter(s => s !== section)
        : [...prev, section]
    );
  };
  
  // Usa useDeviceMode hook per rilevamento desktop più affidabile
  const { isDesktop: isDesktopView } = useDeviceMode();
  const isPwaRoute = typeof window !== 'undefined' && window.location.pathname.startsWith('/app/');
   
    // Fetch item data
    console.log('[DEBUG ItemDetail] itemId:', itemId, 'itemType:', itemType);
    const endpoint = itemType === 'boats' ? 'boats' : itemType === 'motors' ? 'motors' : 'accessories';
    const { data: item, isLoading, refetch, error } = useQuery(
      ['item', itemType, itemId],
      () => {
        console.log('[DEBUG ItemDetail] Fetching item from API...');
        if (itemType === 'boats') {
          return api.get(`/${endpoint}/${itemId}`).then((res) => {
            console.log('[DEBUG ItemDetail] API response for boats:', res);
            console.log('[DEBUG ItemDetail] res.data:', res.data);
            console.log('[DEBUG ItemDetail] res.data?.data:', res.data?.data);
            console.log('[DEBUG ItemDetail] optional_features:', res.data?.data?.optional_features);
            return res.data?.data || res.data;
          });
        }
        if (itemType === 'motors') {
          return motorApi.getMotor(itemId).then((res) => {
            console.log('[DEBUG ItemDetail] API response for motors:', res);
            console.log('[DEBUG ItemDetail] res.data.data:', res.data.data);
            console.log('[DEBUG ItemDetail] optional_features:', res.data.data?.optional_features);
            return res.data.data;
          });
        }
        // Accessories
        return api.get(`/${endpoint}/${itemId}`).then((res) => {
          console.log('[DEBUG ItemDetail] API response for accessories:', res);
          return res.data?.data || res.data;
        });
      },
      {
        enabled: itemId > 0,
        staleTime: 0,
        onError: (err) => {
          console.error('[ItemDetail] Error loading item:', err);
        }
      }
    );

   // Page title context for mobile header
   const { setTitle, resetTitle } = usePageTitle();

   // Imposta il titolo della pagina (usato da MobileHeader)
   useEffect(() => {
     if (item) {
       const title = `${item.brand || ''} ${item.model || ''}`.trim();
       if (title) {
         setTitle(title);
       } else {
         resetTitle();
       }
     } else {
       resetTitle();
     }
   }, [item, setTitle, resetTitle]);

   // Redirect to list if item is sold (only after loading is complete)
   useEffect(() => {
     if (item && !isLoading && item.status === 'sold') {
       toast.error('Questo elemento è stato venduto e non è più modificabile');
       navigate('/' + (itemType === 'boats' ? 'boats' : 'motors'));
     }
   }, [item, isLoading, navigate, itemType]);

  // Tracking - registra visualizzazione quando l'item viene caricato
  const { trackView, trackClick } = useTracking();

  // Track public view for unauthenticated users
  useEffect(() => {
    if (item && itemId && !isAuthenticated && hasCookieConsent()) {
      const itmType = itemType === 'boats' ? 'boat' : 'motor';
      trackPublicView(itemId as number, itmType).catch(console.error);
    }
  }, [item, itemId, itemType, isAuthenticated]);

  useEffect(() => {
    if (item && itemId) {
      const itmType = itemType === 'boats' ? 'boat' : 'motor';
      trackView({
        item_type: itmType,
        item_id: itemId as number,
        viewer_region: item.region || '',
        viewer_provincia: item.provincia || ''
      }).catch(console.error);
      
      // Also track public click for unauthenticated users
      if (!isAuthenticated && hasCookieConsent()) {
        trackPublicClick(itemId as number, itmType, 'contact').catch(console.error);
      }
      
      trackClick({
        item_type: itmType,
        item_id: itemId as number,
        click_type: 'contact',
        viewer_region: item.region || '',
        viewer_provincia: item.provincia || ''
      }).catch(console.error);
    }
  }, [item, itemId, itemType]);

  // Funzione helper per tracciare i click
  const handleTrackClick = (clickType: 'contact' | 'quote' | 'share' | 'favorite' | 'call' | 'map' | 'image' | 'video') => {
    if (item && itemId) {
      trackClick({
        item_type: itemType === 'boats' ? 'boat' : 'motor',
        item_id: itemId as number,
        click_type: clickType,
        viewer_region: item?.region || '',
        viewer_provincia: item?.provincia || ''
      }).catch(console.error);
    }
  };
  
  // Fetch位置上 - usa sessionStorage
  useEffect(() => {
    const searchParams = new URLSearchParams(window.location.search);
    
    if (searchParams.get('fixed_location') === 'true') {
      setUseFixedLocation(true);
      return;
    }
    
    // No sessionStorage - default to fixed location
    setUseFixedLocation(true);
  }, []);
  
  // Calcola posizione attiva
  const getActiveLocation = () => {
    if (useFixedLocation) return DEFAULT_COORDS;
    if (location) return location;
    return DEFAULT_COORDS;
  };
  
  // Calcola distanza
  const itemDistance = useMemo((): number | null => {
    if (!item) return null;
    
    const activeLocation = getActiveLocation();
    const itemLat = parseFloat(item.lat) || parseFloat(item.dealer_lat);
    const itemLng = parseFloat(item.lng) || parseFloat(item.dealer_lng);
    
    if (!itemLat || !itemLng) return null;
    
    return calcDistance(activeLocation.lat, activeLocation.lng, itemLat, itemLng);
  }, [item?.id, item?.lat, item?.lng, item?.dealer_lat, item?.dealer_lng, useFixedLocation, location]);
  
  // Mutation per trattativa - normalize itemType to boat_id/motor_id/accessory_id
  const getItemIdField = () => itemType === 'boats' ? 'boat_id' : itemType === 'motors' ? 'motor_id' : 'accessory_id';
  
  const tradeMutation = useMutation(
    (data: { proposed_price: number; message: string }) =>
      api.post('/trades', {
        [getItemIdField()]: itemId,
        ...data
      }),
    {
      onSuccess: () => {
        toast.success('Trattativa inviata!');
        setShowTradeModal(false);
        navigate('/trades');
      },
      onError: (error: any) => {
        toast.error(error.response?.data?.error || 'Errore nella trattativa');
      }
    }
  );
  
  // Mutation per share link
  const shareMutation = useMutation(
    (data: { expires_hours: number }) => {
      const payload = {
        [`${itemType.slice(0, -1)}_id`]: itemId,
        ...data
      };
      return api.post('/share', payload);
    },
    {
      onSuccess: (response) => {
        setGeneratedLink(response.data.data?.share_url || response.data?.share_url || '');
        setShareData(response.data.data || response.data);
        toast.success('Link creato!');
      },
      onError: (error: any) => {
        console.error('[SHARE-DEBUG] Error response:', error.response?.data);
        toast.error(error.response?.data?.message || 'Errore nella creazione del link');
      }
    }
  );
  
  // Mutation per mark-sold
  const markSoldMutation = useMutation(
    () => api.post(`/${itemType}/${itemId}/mark-sold`),
    {
      onSuccess: () => {
        toast.success(`${config.singularCapitalized} segnato come venduto!`);
        setShowMarkSoldModal(false);
        refetch();
      },
      onError: (error: any) => {
        toast.error(error.response?.data?.message || 'Errore nel segnare come venduto');
      }
    }
  );
   
  // Condividi su WhatsApp
  const shareWhatsApp = () => {
    if (!item) return;
    
    const priceText = formatPrice(item.retail_price || item.dealer_price);
    const details = [item.brand, item.model].filter(Boolean).join(' ');
    const yearText = item.year ? `, ${item.year}` : '';
    const shareLink = generatedLink || `${window.location.origin}/${itemType}/${item.slug || item.id}`;
    
    const itemTypeLabel = itemType === 'boats' ? 'barca' : itemType === 'motors' ? 'motore' : 'accessorio';
    const itemTypeEmoji = itemType === 'boats' ? '🚤' : itemType === 'motors' ? '⚙️' : '📦';
    
    const text = config.singular === 'barca' 
      ? `🚤 *SEA LEVEL - La tua barca ti aspetta!*\n\n📋 *${details}${yearText}*\n💰 *Prezzo:* ${priceText}\n${item.location ? `📍 *Località:* ${item.location}\n` : ''}🔗 *Guarda la barca:*\n${shareLink}\n\n💙 Sea Level`
      : `${itemTypeEmoji} *SEA LEVEL - Il tuo ${itemTypeLabel} ti aspetta!*\n\n📋 *${details}${yearText}*\n💰 *Prezzo:* ${priceText}\n${item.location ? `📍 *Località:* ${item.location}\n` : ''}🔗 *Guarda il ${itemTypeLabel}:*\n${shareLink}\n\n💙 Sea Level`;
    
    window.open(`https://wa.me/?text=${encodeURIComponent(text)}`, '_blank');
  };
  
  // Submit trattativa
  const handleTradeSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!tradePrice) return;
    
    tradeMutation.mutate({
      proposed_price: parseFloat(tradePrice),
      message: tradeMessage,
    });
  };
  
  if (isLoading) {
    return (
      <div className="flex justify-center py-12">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    );
  }
  
  if (!item) {
    return (
      <div className="text-center py-12">
        <p className="text-gray-500">{config.singularCapitalized} non trovato</p>
      </div>
    );
  }
  
  // Check if current user is the owner (dealer who listed the item)
  // Use user.dealer_id (dealer table ID) to compare with item.dealer_id
  // Note: user.id is WordPress user ID which is DIFFERENT from dealer_id!
  const itemDealerId = item?.dealer_id != null ? String(item.dealer_id) : null;
  const userDealerId = user?.dealer_id != null ? String(user.dealer_id) : (user?.user_id != null ? String(user.user_id) : null);
  const isOwnItem = userDealerId !== null && itemDealerId !== null && userDealerId === itemDealerId;
  
  // Calcola totale optional (barche nuove con optional, accessori con caratteristiche)
  const totalOptional = (): number => {
    if (!item) return 0;
    if (!item.optional_features) return 0;
    if (itemType === 'boats' && item.is_new_boat !== true) return 0;
    return item.optional_features.reduce((sum: number, opt: any) => {
      return sum + (opt.added_price || 0);
    }, 0);
  };
  
  // Prezzo configurazione (solo per nuovo) - USA RETAIL PRICE
  const configurationPrice = (): number | null => {
    if (!item) return null;
    if (item.is_new_boat !== true) return null;
    const retailPrice = item.retail_price || 0;
    return retailPrice + totalOptional();
  };
  
  // Label dinamico per prezzo (Dealer/Seller)
  const priceLabel = showPrices ? 'Prezzo Dealer' : 'Contatta il Seller';
  const sellerLabel = showPrices ? 'VENDITORE' : 'SELLER';
  const priceButtonText = showPrices ? 'Nascondi prezzi' : 'Mostra prezzi';

  // Estrai immagini e video
  const images = item.images && Array.isArray(item.images) 
    ? item.images.map((img: string | { url: string }) => typeof img === 'string' ? img : img.url)
    : [];
  
  // Estrai video - combina sia video URL (item.video) che video caricati (item.videos)
  const videoArray: string[] = [];
  
  // Aggiungi video URL (singular) se presente
  if (item.video && typeof item.video === 'string' && item.video.trim() !== '') {
    videoArray.push(item.video.trim());
  }
  
  // Aggiungi video caricati (plural) se presenti
  if (item.videos && Array.isArray(item.videos)) {
    item.videos.forEach((vid: string | { url: string }) => {
      const videoUrl = typeof vid === 'string' ? vid : vid.url;
      if (videoUrl && !videoArray.includes(videoUrl)) {
        videoArray.push(videoUrl);
      }
    });
  }
  
  const videos = videoArray;
  
  // Renderizza campi specifici
  const renderSpecificFields = () => {
    if (itemType === 'boats') {
      return (
        <>
          {item.length && (
            <div className="flex items-center gap-2 text-gray-600">
              <RulerLucideIcon className="h-5 w-5" />
              <span>{item.length} m</span>
            </div>
          )}
          {item.width && (
            <div className="flex items-center gap-2 text-gray-600">
              <RulerLucideIcon className="h-5 w-5 rotate-90" />
              <span>{item.width} m</span>
            </div>
          )}
        </>
      );
    }
    
    // Motori
    return (
      <>
        {item.hours_used !== undefined && item.hours_used !== null && (
          <div className="flex items-center gap-2 text-gray-600">
            <ClockLucideIcon className="h-5 w-5" />
            <span>{item.hours_used} ore</span>
          </div>
        )}
        {item.horsepower && (
          <div className="flex items-center gap-2 text-gray-600">
            <ZapLucideIcon className="h-5 w-5" />
            <span>{item.horsepower} HP</span>
          </div>
        )}
        {item.cylinders && (
          <div className="flex items-center gap-2 text-gray-600">
            <SettingsLucideIcon className="h-5 w-5" />
            <span>{item.cylinders} cilindri</span>
          </div>
        )}
        {item.displacement && (
          <div className="flex items-center gap-2 text-gray-600">
            <GaugeLucideIcon className="h-5 w-5" />
            <span>{item.displacement} cc</span>
          </div>
        )}
        {item.weight && (
          <div className="flex items-center gap-2 text-gray-600">
            <WeightLucideIcon className="h-5 w-5" />
            <span>{item.weight} kg</span>
          </div>
        )}
      </>
    );
  };

  // Desktop layout - 3 colonne: 40% galleria, 48% descrizioni, 12% azioni
  if (isDesktopView) {
    return (
      <div className="desktop-item-detail" data-testid="item-detail">
        {/* Colonna 1 (40%) - Galleria Preview */}
        <div className="desktop-col-gallery w-2/5 p-2 bg-gray-100 flex flex-col">
          <DesktopItemGallery 
            images={images} 
            videos={videos}
            title={item.title}
            distance={itemDistance ?? undefined}
            status={item.status}
            onBack={() => navigate(-1)}
          />
        </div>

        {/* Colonna 2 (48%) - Dati Principali */}
        <div className="desktop-col-content w-[48%] bg-gray-50 p-3">
           {/* NUOVA Navigazione semplificata - 4 sezioni */}
           {(itemType === 'boats' || itemType === 'accessories') && (
             <div className="sticky top-0 z-10 bg-gray-50 -mx-3 px-3 py-2 border-b border-gray-200 mb-3">
               <div className="flex gap-1 overflow-x-auto scrollbar-hide pb-1">
                 {detailSections.map((section) => {
                   const Icon = section.icon;
                   return (
                     <button
                       key={section.id}
                       type="button"
                       onClick={() => {
                         setActiveSection(section.id);
                         if (section.id === 'info') {
                           window.scrollTo({ top: 0, behavior: 'smooth' });
                           return;
                         }
                         let sectionId = '';
                         if (section.id === 'specs') sectionId = 'section-specs';
                         else if (section.id === 'motors') sectionId = 'section-motors';
                         else if (section.id === 'tech') sectionId = 'section-fuel';
                         else if (section.id === 'optionals') sectionId = 'section-optional';
                         else if (section.id === 'accessories') sectionId = 'section-accessories';
                         else if (section.id === 'techdetails') sectionId = 'section-tech-details';
                         
                         if (sectionId) {
                           const element = document.getElementById(sectionId);
                           if (element) {
                             element.scrollIntoView({ behavior: 'smooth', block: 'start' });
                           }
                         }
                       }}
                        style={{ borderRadius: '6px' }}
                        className={`px-3 py-1.5 text-xs font-medium whitespace-nowrap transition-all border flex items-center gap-1.5 ${
                         activeSection === section.id
                           ? section.color
                           : 'bg-gray-100 text-gray-600 hover:bg-gray-200 border-gray-200'
                       }`}
                     >
                       <Icon className="w-3.5 h-3.5" />
                       {section.label}
                     </button>
                   );
                 })}
               </div>
             </div>
           )}
          
          <div className="space-y-3 max-w-2xl">
            {/* Title */}
            <div>
              <h1 className="text-sm font-bold text-gray-900 mb-1">{item.title}</h1>
             <p className="text-gray-600 text-xs uppercase font-medium">
                 {item.category || (itemType === 'boats' ? 'BARCA' : itemType === 'motors' ? 'MOTORE' : 'ACCESSORIO')}
               </p>
            </div>

            {/* Prices - Comportamento diverso per NUOVO vs USATA */}
            <div className="flex items-baseline gap-4">
              <div className="flex-1">
                {item.is_new_boat === true ? (
                  // BLOCCO NUOVO: Mostra configurazione
                  <div className="bg-green-50 rounded-xl p-4 border border-green-200">
                    <p className="text-xs text-green-700 font-medium mb-1">
                      Prezzo Configurazione
                    </p>
                    <p className="text-2xl font-bold text-green-700">
                      €{configurationPrice()?.toLocaleString('it-IT')}
                    </p>
                    <p className="text-xs text-green-600 mt-1">
                      €{item.retail_price?.toLocaleString('it-IT')} (base) + €{totalOptional().toLocaleString('it-IT')} (optional)
                    </p>
                  </div>
                ) : (
                  // BLOCCO USATA: Mostra prezzi dal database
                  <>
                    {/* Per utenti pubblici: mostra solo prezzo retail */}
                    {isPublicView ? (
                      <div>
                        <p className="text-xs text-gray-500">Prezzo al Pubblico</p>
                        <p className="text-base font-bold text-primary-600">
                          {formatPrice(item.retail_price || item.dealer_price)}
                        </p>
                      </div>
                    ) : (
                      // Per dealer: mostra tutto
                      <>
                        {showPrices ? (
                          <>
                            <div className="flex items-center justify-between mb-1">
                              <p className="text-xs text-gray-500">{priceLabel}</p>
                              <button
                                onClick={() => setShowPrices(!showPrices)}
                                className="flex items-center gap-1 px-2 py-0.5 rounded-full hover:bg-gray-100 text-gray-500 transition-colors text-xs border border-gray-200"
                                title="Nascondi prezzi"
                              >
                                <EyeOffLucideIcon className="w-3 h-3" />
                                <span className="hidden sm:inline">Nascondi</span>
                              </button>
                            </div>
                            <p className="text-base font-bold text-primary-600">
                              {formatPrice(item.dealer_price)}
                            </p>
                          </>
                        ) : (
                          <div className="flex flex-col gap-2">
                            <div className="flex items-center justify-between">
                              <p className="text-xs text-gray-500">{priceLabel}</p>
                              <button
                                onClick={() => setShowPrices(!showPrices)}
                                className="flex items-center gap-1 px-2 py-1 rounded-full hover:bg-gray-100 text-gray-500 transition-colors text-xs border border-gray-200"
                                title="Mostra prezzi"
                              >
                                <EyeLucideIcon className="w-4 h-4" />
                                <span className="hidden sm:inline">Mostra</span>
                              </button>
                            </div>
                            <p className="text-sm text-gray-600 italic">
                              Contatta il {sellerLabel.toLowerCase()} per il prezzo
                            </p>
                          </div>
                        )}
                        {item.retail_price && (
                          <div>
                            <p className="text-sm text-gray-500">Prezzo al Pubblico</p>
                            <p className="text-base font-semibold text-gray-700">
                              {formatPrice(item.retail_price)}
                            </p>
                          </div>
                        )}
                        {(() => {
                          const profitPercentage = calculateProfitPercentage(item.dealer_price, item.retail_price);
                          return profitPercentage !== null ? (
                            <div className="flex flex-col items-start">
                              <p className="text-sm text-gray-500">Guadagno</p>
                              <p className="text-sm font-bold text-green-600">
                                +{profitPercentage}%
                              </p>
                            </div>
                          ) : null;
                        })()}
                      </>
                    )}
                  </>
                )}
              </div>
            </div>

            {/* Market Price Benchmark */}
            {itemType === 'boats' && item.is_new_boat !== true && (
              <MarketPrice boatId={item.id} price={item.dealer_price || item.retail_price || undefined} />
            )}

            {/* Dealer Info Card */}
            {(item.dealer_name || item.dealer_logo) && (
              <div className="dealer-info-card">
                <div className="flex items-start gap-4">
                  {item.dealer_logo ? (
                    <div className="dealer-logo-container">
                      <img 
                        src={fixImageUrl(item.dealer_logo)} 
                        alt={item.dealer_name} 
                        className="dealer-logo"
                        style={{maxWidth: '200px', maxHeight: '140px'}}
                        width={200}
                        height={140}
                      />
                    </div>
                  ) : (
                    <div className="w-16 h-16 rounded-lg flex items-center justify-center" style={{background: 'var(--glass-primary-light, rgba(0, 153, 255, 0.15))'}}>
                      {itemType === 'boats' ? (
                        <AnchorLucideIcon className="w-8 h-8" style={{color: 'var(--cal-link-blue, #0099ff)'}} />
                      ) : (
                        <ZapLucideIcon className="w-8 h-8" style={{color: 'var(--cal-link-blue, #0099ff)'}} />
                      )}
                    </div>
                  )}
                  <div className="flex-1 min-w-0">
                    <p className="text-xs font-medium uppercase tracking-wide" style={{color: 'var(--cal-link-blue, #0099ff)'}}>{sellerLabel}</p>
                    <p className="text-lg font-bold truncate" style={{color: 'var(--cal-text-primary, #242424)'}}>{item.dealer_name}</p>
                    {item.location && (
                      <p className="text-sm flex items-center gap-1 truncate" style={{color: 'var(--cal-text-secondary, #898989)'}}>
                        <MapPinLucideIcon className="w-3 h-3 flex-shrink-0" />
                        {item.location}
                      </p>
                    )}
                    {itemDistance !== null && (
                      <p className="text-sm font-medium mt-1" style={{color: 'var(--cal-link-blue, #0099ff)'}}>
                        {itemDistance} km da te
                      </p>
                    )}
                  </div>
                  <div className="flex flex-col gap-2">
                    {item.dealer_phone && (
                      <a href={`tel:${item.dealer_phone}`} className="flex items-center gap-2 px-4 py-2 rounded-lg hover:opacity-90 transition-glass" style={{background: 'var(--cal-charcoal, #242424)', color: '#ffffff'}}>
                        <PhoneLucideIcon className="w-4 h-4" />
                        <span className="text-sm font-medium">Chiama</span>
                      </a>
                    )}
                    {item.dealer_phone && (
                      <a href={`https://wa.me/${(() => { const d = item.dealer_phone.replace(/\D/g, ''); return d.startsWith('39') ? d : '39' + d; })()}`} target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 px-4 py-2 rounded-lg hover:opacity-90 transition-opacity" style={{background: '#25D366', color: '#ffffff'}}>
                        <MessageCircleLucideIcon className="w-4 h-4" />
                        <span className="text-sm font-medium">WhatsApp</span>
                      </a>
                    )}
                    {item.dealer_email && (
                      <a href={`mailto:${item.dealer_email}`} className="flex items-center gap-2 px-4 py-2 rounded-lg hover:bg-gray-200 transition-colors" style={{background: 'var(--cal-bg-secondary, #f5f5f5)', color: 'var(--cal-text-primary, #242424)'}}>
                        <MailLucideIcon className="w-4 h-4" />
                        <span className="text-sm font-medium">Email</span>
                      </a>
                    )}
                         {!isAuthenticated && (
                           <QuoteRequestButton 
                             itemId={item.id} 
                             itemType={itemType === 'boats' ? 'boat' : itemType === 'motors' ? 'motor' : 'accessory'}
                             brand={item.brand}
                             model={item.model}
                           />
                         )}
                  </div>
                </div>
              </div>
            )}

            {/* Description - Always visible after dealer info */}
            {item.description && (
              <div className="bg-white rounded-xl p-4 border border-gray-200 shadow-sm">
                <h3 className="font-semibold text-gray-900 mb-2 text-sm flex items-center gap-2"><FileLucideIcon className="w-4 h-4" /> Descrizione</h3>
                <ItemDescription text={item.description} className="text-gray-600 text-sm" />
              </div>
            )}

            {/* Specs Grid - Dettagli Tecnici */}
            <div className="bg-white rounded-xl p-3 border border-gray-200 shadow-sm">
              <h3 className="font-semibold text-gray-900 mb-2 text-xs" style={{color: 'var(--color-primary, #242424)'}}>Dettagli Tecnici</h3>
              <div className="grid grid-cols-2 gap-x-3 gap-y-1 text-[10px]">
                {item.year && (
                  <div className="flex items-center gap-2">
                    <CalendarLucideIcon className="h-4 w-4 text-gray-400" />
                    <span className="text-gray-700">{item.year}</span>
                  </div>
                )}
                {item.engine_type && (
                  <div className="flex items-center gap-2">
                    <NavigationLucideIcon className="h-4 w-4 text-gray-400" />
                    <span className="text-gray-700">{item.engine_type} {item.engine_power && `${item.engine_power}CV`}</span>
                  </div>
                )}
                {item.fuel_type && (
                  <div className="flex items-center gap-2">
                    <FuelLucideIcon className="h-4 w-4 text-gray-400" />
                    <span className="text-gray-700">{item.fuel_type}</span>
                  </div>
                )}
                {item.condition && (
                  <div className="flex items-center gap-2">
                    <GaugeLucideIcon className="h-4 w-4 text-gray-400" />
                    <span className="text-gray-700">{item.condition}</span>
                  </div>
                )}
                {renderSpecificFields()}
              </div>
            </div>

            {/* Boat Technical Specs - Show on specs or techdetails tab */}
            {(activeSection === 'specs' || activeSection === 'techdetails') && itemType === 'boats' && (
              <div className="bg-white rounded-xl p-3 border border-gray-200 shadow-sm" id="section-boat">
                <h3 className="font-semibold text-gray-900 mb-2 text-xs flex items-center gap-2" style={{color: 'var(--color-primary, #242424)'}}><ShipIcon className="w-4 h-4" /> Dati Barca</h3>
                <div className="grid grid-cols-2 gap-x-3 gap-y-1 text-[10px]">
                  {/* Dimensioni - Show on boat tab or essential or mobile */}
                  {(item.length_m || item.length) && (
                    <div className="flex justify-between">
                      <span className="text-gray-500">Lunghezza</span>
                      <span className="text-gray-900 font-medium">{item.length_m || item.length}m {item.length_ft ? `(${item.length_ft}ft)` : ''}</span>
                    </div>
                  )}
                  {(item.beam_m || item.width) && (
                    <div className="flex justify-between">
                      <span className="text-gray-500">Larghezza</span>
                      <span className="text-gray-900 font-medium">{item.beam_m || item.width}m {item.beam_ft ? `(${item.beam_ft}ft)` : ''}</span>
                    </div>
                  )}
                  {item.draft_m && (
                    <div className="flex justify-between">
                      <span className="text-gray-500">Pescaggio</span>
                      <span className="text-gray-900 font-medium">{item.draft_m}m {item.draft_ft ? `(${item.draft_ft}ft)` : ''}</span>
                    </div>
                  )}
                  
                  {/* Capacità */}
                  {item.cabins_count && (
                    <div className="flex justify-between">
                      <span className="text-gray-500">Cabine</span>
                      <span className="text-gray-900 font-medium">{item.cabins_count}</span>
                    </div>
                  )}
                  {item.bathrooms_count && (
                    <div className="flex justify-between">
                      <span className="text-gray-500">Bagni</span>
                      <span className="text-gray-900 font-medium">{item.bathrooms_count}</span>
                    </div>
                  )}
                  {item.guests_max && (
                    <div className="flex justify-between">
                      <span className="text-gray-500">Ospiti</span>
                      <span className="text-gray-900 font-medium">{item.guests_max}</span>
                    </div>
                  )}
                  {item.crew_max && (
                    <div className="flex justify-between">
                      <span className="text-gray-500">Equipaggio</span>
                      <span className="text-gray-900 font-medium">{item.crew_max}</span>
                    </div>
                  )}

                  {/* Motori */}
                  {item.engines_brand && (
                    <div className="flex justify-between">
                      <span className="text-gray-500">Motori</span>
                      <span className="text-gray-900 font-medium">{item.engines_brand} {item.engines_model}</span>
                    </div>
                  )}
                  {item.engines_count && (
                    <div className="flex justify-between">
                      <span className="text-gray-500">Numero Motori</span>
                      <span className="text-gray-900 font-medium">{item.engines_count}</span>
                    </div>
                  )}
                  {item.engines_power_hp && (
                    <div className="flex justify-between">
                      <span className="text-gray-500">Potenza</span>
                      <span className="text-gray-900 font-medium">{item.engines_power_hp} HP</span>
                    </div>
                  )}
                  {item.engines_hours && (
                    <div className="flex justify-between">
                      <span className="text-gray-500">Ore Motore</span>
                      <span className="text-gray-900 font-medium">{item.engines_hours}</span>
                    </div>
                  )}
                  {item.transmission_type && (
                    <div className="flex justify-between">
                      <span className="text-gray-500">Trasmissione</span>
                      <span className="text-gray-900 font-medium">{item.transmission_type}</span>
                    </div>
                  )}

                  {/* Prestazioni */}
                  {item.max_speed_knots && (
                    <div className="flex justify-between">
                      <span className="text-gray-500">Vel. Max</span>
                      <span className="text-gray-900 font-medium">{item.max_speed_knots} nodi</span>
                    </div>
                  )}
                  {item.cruising_speed_knots && (
                    <div className="flex justify-between">
                      <span className="text-gray-500">Vel. Crociera</span>
                      <span className="text-gray-900 font-medium">{item.cruising_speed_knots} nodi</span>
                    </div>
                  )}
                  {item.range_nm && (
                    <div className="flex justify-between">
                      <span className="text-gray-500">Autonomia</span>
                      <span className="text-gray-900 font-medium">{item.range_nm} nm</span>
                    </div>
                  )}
                  {item.engines_count && (
                    <div className="flex justify-between">
                      <span className="text-gray-500">Numero Motori</span>
                      <span className="text-gray-900 font-medium">{item.engines_count}</span>
                    </div>
                  )}
                  {item.engines_power_hp && (
                    <div className="flex justify-between">
                      <span className="text-gray-500">Potenza</span>
                      <span className="text-gray-900 font-medium">{item.engines_power_hp} HP</span>
                    </div>
                  )}
                  {item.engines_hours && (
                    <div className="flex justify-between">
                      <span className="text-gray-500">Ore Motore</span>
                      <span className="text-gray-900 font-medium">{item.engines_hours}</span>
                    </div>
                  )}
                  {item.transmission_type && (
                    <div className="flex justify-between">
                      <span className="text-gray-500">Trasmissione</span>
                      <span className="text-gray-900 font-medium">{item.transmission_type}</span>
                    </div>
                  )}

                  {/* Prestazioni */}
                  {item.max_speed_knots && (
                    <div className="flex justify-between">
                      <span className="text-gray-500">Vel. Max</span>
                      <span className="text-gray-900 font-medium">{item.max_speed_knots} nodi</span>
                    </div>
                  )}
                  {item.cruising_speed_knots && (
                    <div className="flex justify-between">
                      <span className="text-gray-500">Vel. Crociera</span>
                      <span className="text-gray-900 font-medium">{item.cruising_speed_knots} nodi</span>
                    </div>
                  )}
                  {item.range_nm && (
                    <div className="flex justify-between">
                      <span className="text-gray-500">Autonomia</span>
                      <span className="text-gray-900 font-medium">{item.range_nm} nm</span>
                    </div>
                  )}

                  {/* Carburante - Show on fuel tab or essential or mobile */}
                  {item.fuel_capacity_liters && (
                    <div className="flex justify-between">
                      <span className="text-gray-500">Serbatoio</span>
                      <span className="text-gray-900 font-medium">{item.fuel_capacity_liters}L</span>
                    </div>
                  )}
                  {item.water_capacity_liters && (
                    <div className="flex justify-between">
                      <span className="text-gray-500">Acqua</span>
                      <span className="text-gray-900 font-medium">{item.water_capacity_liters}L</span>
                    </div>
                  )}

                  {/* Costruzione - Show on boat tab or essential or mobile */}
                  {item.hull_material && (
                    <div className="flex justify-between">
                      <span className="text-gray-500">Scafo</span>
                      <span className="text-gray-900 font-medium">{item.hull_material}</span>
                    </div>
                  )}
                  {item.boat_type && (
                    <div className="flex justify-between">
                      <span className="text-gray-500">Tipo</span>
                      <span className="text-gray-900 font-medium">{item.boat_type}</span>
                    </div>
                  )}
                  {/* Tipologia Codice - mostra il nome completo dalla lista tipologie */}
                  {item.boat_type_code && itemType === 'boats' && (
                    <div className="flex justify-between">
                      <span className="text-gray-500">Tipologia</span>
                      <span className="text-gray-900 font-medium">
                        {BOAT_TYPES.find(t => t.code === item.boat_type_code)?.nome || item.boat_type_code}
                      </span>
                    </div>
                  )}
                  {item.cabin_layout && (
                    <div className="flex justify-between">
                      <span className="text-gray-500">Layout</span>
                      <span className="text-gray-900 font-medium">{item.cabin_layout}</span>
                    </div>
                  )}

                  {/* Impianti */}
                  {item.air_conditioning && (
                    <div className="flex justify-between">
                      <span className="text-gray-500">Aria Condiz.</span>
                      <span className="text-gray-900 font-medium">✓</span>
                    </div>
                  )}
                  {item.generator_brand && (
                    <div className="flex justify-between">
                      <span className="text-gray-500">Generatore</span>
                      <span className="text-gray-900 font-medium">{item.generator_brand} {item.generator_power_kw}kW</span>
                    </div>
                  )}
                  {item.stern_thruster && (
                    <div className="flex justify-between">
                      <span className="text-gray-500">Elica Prua</span>
                      <span className="text-gray-900 font-medium">✓ {item.stern_thruster_power_hp ? `(${item.stern_thruster_power_hp}HP)` : ''}</span>
                    </div>
                  )}
                  {item.stabilizing_fins && (
                    <div className="flex justify-between">
                      <span className="text-gray-500">Stabilizzatori</span>
                      <span className="text-gray-900 font-medium">✓ {item.fins_zero_speed ? '(Zero Speed)' : ''}</span>
                    </div>
                  )}
                  {item.water_maker && (
                    <div className="flex justify-between">
                      <span className="text-gray-500">Dissalatore</span>
                      <span className="text-gray-900 font-medium">✓</span>
                    </div>
                  )}
                  {/* Esterni - Show on exterior tab or essential or mobile */}
                  {item.teak_deck && (
                    <div className="flex justify-between">
                      <span className="text-gray-500">Ponte Teak</span>
                      <span className="text-gray-900 font-medium">✓</span>
                    </div>
                  )}
                  {item.flybridge && (
                    <div className="flex justify-between">
                      <span className="text-gray-500">Flybridge</span>
                      <span className="text-gray-900 font-medium">✓</span>
                    </div>
                  )}
                  {/* Propeller */}
                  {item.propeller_type && (
                    <div className="flex justify-between">
                      <span className="text-gray-500">Elica</span>
                      <span className="text-gray-900 font-medium">{item.propeller_type}</span>
                    </div>
                  )}
                  {/* Fuel tanks */}
                  {item.fuel_tanks_count && (
                    <div className="flex justify-between">
                      <span className="text-gray-500">Serbatoi Carburante</span>
                      <span className="text-gray-900 font-medium">{item.fuel_tanks_count}x {item.fuel_tanks_material || ''}</span>
                    </div>
                  )}
                  {/* Water tanks config */}
                  {item.water_tanks_config && (
                    <div className="flex justify-between">
                      <span className="text-gray-500">Serbatoi Acqua</span>
                      <span className="text-gray-900 font-medium">{item.water_tanks_config}</span>
                    </div>
                  )}
                  {/* Autoclave */}
                  {item.autoclave && (
                    <div className="flex justify-between">
                      <span className="text-gray-500">Autoclave</span>
                      <span className="text-gray-900 font-medium">✓</span>
                    </div>
                  )}
                  {/* Boiler */}
                  {item.boiler_present && item.boiler_type && (
                    <div className="flex justify-between">
                      <span className="text-gray-500">Boiler</span>
                      <span className="text-gray-900 font-medium">{item.boiler_type}</span>
                    </div>
                  )}
                  {/* Voltage */}
                  {item.voltage_config && (
                    <div className="flex justify-between">
                      <span className="text-gray-500">Voltaggio</span>
                      <span className="text-gray-900 font-medium">{item.voltage_config}</span>
                    </div>
                  )}
                  {/* Batteries */}
                  {item.batteries_count && (
                    <div className="flex justify-between">
                      <span className="text-gray-500">Batterie</span>
                      <span className="text-gray-900 font-medium">{item.batteries_count}x {item.batteries_capacity_ah ? `${item.batteries_capacity_ah}Ah` : ''}</span>
                    </div>
                  )}
                  {/* Battery charger */}
                  {item.battery_charger && (
                    <div className="flex justify-between">
                      <span className="text-gray-500">Carica Batterie</span>
                      <span className="text-gray-900 font-medium">✓</span>
                    </div>
                  )}
                  {/* Inverter */}
                  {item.inverter && (
                    <div className="flex justify-between">
                      <span className="text-gray-500">Inverter</span>
                      <span className="text-gray-900 font-medium">{item.inverter_power_watts ? `${item.inverter_power_watts}W` : '✓'}</span>
                    </div>
                  )}
                  {/* Navigation - Show on electronics tab or essential or mobile */}
                  {item.radar_model && (
                    <div className="flex justify-between">
                      <span className="text-gray-500">Radar</span>
                      <span className="text-gray-900 font-medium">{item.radar_model}</span>
                    </div>
                  )}
                  {item.autopilot_model && (
                    <div className="flex justify-between">
                      <span className="text-gray-500">Pilota Automatico</span>
                      <span className="text-gray-900 font-medium">{item.autopilot_model}</span>
                    </div>
                  )}
                  {item.ais_system && (
                    <div className="flex justify-between">
                      <span className="text-gray-500">AIS</span>
                      <span className="text-gray-900 font-medium">✓</span>
                    </div>
                  )}
                  {item.plotter_screen_inches && (
                    <div className="flex justify-between">
                      <span className="text-gray-500">Plotter</span>
                      <span className="text-gray-900 font-medium">{item.plotter_screen_inches}"</span>
                    </div>
                  )}
                  {/* Entertainment - Show on entertainment tab or essential or mobile */}
                  {item.tv_count && (
                    <div className="flex justify-between">
                      <span className="text-gray-500">TV</span>
                      <span className="text-gray-900 font-medium">{item.tv_count}</span>
                    </div>
                  )}
                  {item.audio_system && (
                    <div className="flex justify-between">
                      <span className="text-gray-500">Audio</span>
                      <span className="text-gray-900 font-medium">{item.audio_system}</span>
                    </div>
                  )}
                  {item.internet_package && (
                    <div className="flex justify-between">
                      <span className="text-gray-500">Internet</span>
                      <span className="text-gray-900 font-medium">{item.internet_package}</span>
                    </div>
                  )}
                  {item.sirius_system && (
                    <div className="flex justify-between">
                      <span className="text-gray-500">Sirius</span>
                      <span className="text-gray-900 font-medium">✓</span>
                    </div>
                  )}
                  {/* Galley - Show on other tab or essential or mobile */}
                  {item.galley_brand && (
                    <div className="flex justify-between">
                      <span className="text-gray-500">Cucina</span>
                      <span className="text-gray-900 font-medium">{item.galley_brand}</span>
                    </div>
                  )}
                  {item.ice_maker && (
                    <div className="flex justify-between">
                      <span className="text-gray-500">Ice Maker</span>
                      <span className="text-gray-900 font-medium">✓ {item.ice_maker_location ? `(${item.ice_maker_location})` : ''}</span>
                    </div>
                  )}
                  {item.waste_mincer && (
                    <div className="flex justify-between">
                      <span className="text-gray-500">Trash</span>
                      <span className="text-gray-900 font-medium">✓</span>
                    </div>
                  )}
                  {item.grill_type && (
                    <div className="flex justify-between">
                      <span className="text-gray-500">Grill</span>
                      <span className="text-gray-900 font-medium">{item.grill_type} {item.grill_location ? `(${item.grill_location})` : ''}</span>
                    </div>
                  )}
                  {/* Exterior */}
                  {item.bimini_type && (
                    <div className="flex justify-between">
                      <span className="text-gray-500">Bimini</span>
                      <span className="text-gray-900 font-medium">{item.bimini_type}</span>
                    </div>
                  )}
                  {item.canopy_carbon_poles && (
                    <div className="flex justify-between">
                      <span className="text-gray-500">Carbon Poles</span>
                      <span className="text-gray-900 font-medium">✓</span>
                    </div>
                  )}
                  {item.teak_caulking_color && (
                    <div className="flex justify-between">
                      <span className="text-gray-500">Teak Color</span>
                      <span className="text-gray-900 font-medium">{item.teak_caulking_color}</span>
                    </div>
                  )}
                  {item.superstructure_material && (
                    <div className="flex justify-between">
                      <span className="text-gray-500">Soprastruttura</span>
                      <span className="text-gray-900 font-medium">{item.superstructure_material}</span>
                    </div>
                  )}
                  {item.deck_material && (
                    <div className="flex justify-between">
                      <span className="text-gray-500">Ponte</span>
                      <span className="text-gray-900 font-medium">{item.deck_material}</span>
                    </div>
                  )}
                  {/* Interior */}
                  {item.interior_style && (
                    <div className="flex justify-between">
                      <span className="text-gray-500">Stile Interno</span>
                      <span className="text-gray-900 font-medium">{item.interior_style}</span>
                    </div>
                  )}
                  {item.flooring_type && (
                    <div className="flex justify-between">
                      <span className="text-gray-500">Pavimentazione</span>
                      <span className="text-gray-900 font-medium">{item.flooring_type}</span>
                    </div>
                  )}
                  {/* Safety */}
                  {item.underwater_lights && (
                    <div className="flex justify-between">
                      <span className="text-gray-500">Luci Sottozero</span>
                      <span className="text-gray-900 font-medium">✓</span>
                    </div>
                  )}
                  {item.cctv_cameras_count && (
                    <div className="flex justify-between">
                      <span className="text-gray-500">Telecamere CCTV</span>
                      <span className="text-gray-900 font-medium">{item.cctv_cameras_count}</span>
                    </div>
                  )}
                  {item.life_raft_capacity && (
                    <div className="flex justify-between">
                      <span className="text-gray-500">Zattera</span>
                      <span className="text-gray-900 font-medium">{item.life_raft_capacity} persone</span>
                    </div>
                  )}
                  {/* Flybridge features */}
                  {item.flybridge_features && (
                    <div className="flex justify-between col-span-2">
                      <span className="text-gray-500">Flybridge</span>
                      <span className="text-gray-900 font-medium">{item.flybridge_features}</span>
                    </div>
                  )}
                </div>
              </div>
            )}
            {/* End boat specs - closed above */}

            {/* SPECIFICHE TECNICHE TIPOLOGIA */}
            {item.boat_type_code && item.technical_specs && (() => {
              const specs = getTechnicalSpecs(item.technical_specs, item.boat_type_code);
              const boatTypeInfo = BOAT_TYPES.find(t => t.code === item.boat_type_code);

              if (specs && specs.length > 0) {
                return (
                  <div className="mt-4 p-4 bg-gray-50 rounded-lg">
                    <h3 className="font-semibold text-sm mb-3 flex items-center gap-2">
                      <ShipIcon className="w-4 h-4" />
                      Specifiche {boatTypeInfo?.nome || item.boat_type_code}
                    </h3>
                    <div className="space-y-2 text-xs">
                      {specs.map((spec, idx) => (
                        <div key={`${spec.label}-${idx}`} className="flex justify-between">
                          <span className="text-gray-500">{spec.label}</span>
                          <span className="text-gray-900 font-medium">
                            {String(spec.value)}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                );
              }
              return null;
            })()}

            {/* ===== SEPARATE SECTION: MOTORI ===== */}
            {(activeSection === 'motors' || activeSection === 'all') && itemType === 'boats' && (
              <div className="bg-white rounded-xl p-3 border border-gray-200 shadow-sm" id="section-motors">
                <h3 className="font-semibold text-gray-900 mb-2 text-xs flex items-center gap-2" style={{color: 'var(--color-primary, #242424)'}}><SettingsLucideIcon className="w-4 h-4" /> Motori & Prestazioni</h3>
                <div className="grid grid-cols-2 gap-x-3 gap-y-1 text-[10px]">
                  {item.engines_brand && (
                    <div className="flex justify-between">
                      <span className="text-gray-500">Motori</span>
                      <span className="text-gray-900 font-medium">{item.engines_brand} {item.engines_model}</span>
                    </div>
                  )}
                  {item.engines_count && (
                    <div className="flex justify-between">
                      <span className="text-gray-500">Numero Motori</span>
                      <span className="text-gray-900 font-medium">{item.engines_count}</span>
                    </div>
                  )}
                  {item.engines_power_hp && (
                    <div className="flex justify-between">
                      <span className="text-gray-500">Potenza</span>
                      <span className="text-gray-900 font-medium">{item.engines_power_hp} HP</span>
                    </div>
                  )}
                  {item.engines_hours && (
                    <div className="flex justify-between">
                      <span className="text-gray-500">Ore Motore</span>
                      <span className="text-gray-900 font-medium">{item.engines_hours}</span>
                    </div>
                  )}
                  {item.transmission_type && (
                    <div className="flex justify-between">
                      <span className="text-gray-500">Trasmissione</span>
                      <span className="text-gray-900 font-medium">{item.transmission_type}</span>
                    </div>
                  )}
                  {item.max_speed_knots && (
                    <div className="flex justify-between">
                      <span className="text-gray-500">Vel. Max</span>
                      <span className="text-gray-900 font-medium">{item.max_speed_knots} nodi</span>
                    </div>
                  )}
                  {item.cruising_speed_knots && (
                    <div className="flex justify-between">
                      <span className="text-gray-500">Vel. Crociera</span>
                      <span className="text-gray-900 font-medium">{item.cruising_speed_knots} nodi</span>
                    </div>
                  )}
                  {item.range_nm && (
                    <div className="flex justify-between">
                      <span className="text-gray-500">Autonomia</span>
                      <span className="text-gray-900 font-medium">{item.range_nm} nm</span>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* ===== SEPARATE SECTION: IMPIANTI ===== */}
            {/* MAPPATURA NUOVA: tech = impianti + esterni + elettronica + optional */}
            {(activeSection === 'tech' || activeSection === 'info') && itemType === 'boats' && (
              <div className="bg-white rounded-xl p-3 border border-gray-200 shadow-sm" id="section-fuel">
                <h3 className="font-semibold text-gray-900 mb-2 text-xs flex items-center gap-2" style={{color: 'var(--color-primary, #242424)'}}><FuelLucideIcon className="w-4 h-4" /> Impianti</h3>
                <div className="grid grid-cols-2 gap-x-3 gap-y-1 text-[10px]">
                  {item.fuel_capacity_liters && (
                    <div className="flex justify-between">
                      <span className="text-gray-500">Serbatoio Carburante</span>
                      <span className="text-gray-900 font-medium">{item.fuel_capacity_liters}L</span>
                    </div>
                  )}
                  {item.fuel_tanks_count && (
                    <div className="flex justify-between">
                      <span className="text-gray-500">Serbatoi Carburante</span>
                      <span className="text-gray-900 font-medium">{item.fuel_tanks_count}x {item.fuel_tanks_material || ''}</span>
                    </div>
                  )}
                  {item.water_capacity_liters && (
                    <div className="flex justify-between">
                      <span className="text-gray-500">Serbatoi Acqua</span>
                      <span className="text-gray-900 font-medium">{item.water_capacity_liters}L</span>
                    </div>
                  )}
                  {item.water_tanks_config && (
                    <div className="flex justify-between">
                      <span className="text-gray-500">Config. Serbatoi</span>
                      <span className="text-gray-900 font-medium">{item.water_tanks_config}</span>
                    </div>
                  )}
                  {item.water_maker && (
                    <div className="flex justify-between">
                      <span className="text-gray-500">Dissalatore</span>
                      <span className="text-gray-900 font-medium">✓</span>
                    </div>
                  )}
                  {item.autoclave && (
                    <div className="flex justify-between">
                      <span className="text-gray-500">Autoclave</span>
                      <span className="text-gray-900 font-medium">✓</span>
                    </div>
                  )}
                  {item.boiler_present && item.boiler_type && (
                    <div className="flex justify-between">
                      <span className="text-gray-500">Boiler</span>
                      <span className="text-gray-900 font-medium">{item.boiler_type}</span>
                    </div>
                  )}
                  {item.voltage_config && (
                    <div className="flex justify-between">
                      <span className="text-gray-500">Voltaggio</span>
                      <span className="text-gray-900 font-medium">{item.voltage_config}</span>
                    </div>
                  )}
                  {item.generator_brand && (
                    <div className="flex justify-between">
                      <span className="text-gray-500">Generatore</span>
                      <span className="text-gray-900 font-medium">{item.generator_brand} {item.generator_power_kw}kW</span>
                    </div>
                  )}
                  {item.batteries_count && (
                    <div className="flex justify-between">
                      <span className="text-gray-500">Batterie</span>
                      <span className="text-gray-900 font-medium">{item.batteries_count}x {item.batteries_capacity_ah ? `${item.batteries_capacity_ah}Ah` : ''}</span>
                    </div>
                  )}
                  {item.battery_charger && (
                    <div className="flex justify-between">
                      <span className="text-gray-500">Carica Batterie</span>
                      <span className="text-gray-900 font-medium">✓</span>
                    </div>
                  )}
                  {item.inverter && (
                    <div className="flex justify-between">
                      <span className="text-gray-500">Inverter</span>
                      <span className="text-gray-900 font-medium">{item.inverter_power_watts ? `${item.inverter_power_watts}W` : '✓'}</span>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* ===== SEPARATE SECTION: ESTERNI ===== */}
            {/* MAPPATURA NUOVA: tech = impianti + esterni + elettronica + optional */}
            {(activeSection === 'tech' || activeSection === 'info') && itemType === 'boats' && (
              <div className="bg-white rounded-xl p-3 border border-gray-200 shadow-sm" id="section-exterior">
                <h3 className="font-semibold text-gray-900 mb-2 text-xs flex items-center gap-2" style={{color: 'var(--color-primary, #242424)'}}><SunLucideIcon className="w-4 h-4" /> Esterni & Sicurezza</h3>
                <div className="grid grid-cols-2 gap-x-3 gap-y-1 text-[10px]">
                  {item.teak_deck && (
                    <div className="flex justify-between">
                      <span className="text-gray-500">Ponte Teak</span>
                      <span className="text-gray-900 font-medium">✓</span>
                    </div>
                  )}
                  {item.teak_caulking_color && (
                    <div className="flex justify-between">
                      <span className="text-gray-500">Teak Color</span>
                      <span className="text-gray-900 font-medium">{item.teak_caulking_color}</span>
                    </div>
                  )}
                  {item.flybridge && (
                    <div className="flex justify-between">
                      <span className="text-gray-500">Flybridge</span>
                      <span className="text-gray-900 font-medium">✓</span>
                    </div>
                  )}
                  {item.flybridge_features && (
                    <div className="flex justify-between col-span-2">
                      <span className="text-gray-500">Flybridge</span>
                      <span className="text-gray-900 font-medium">{item.flybridge_features}</span>
                    </div>
                  )}
                  {item.bimini_type && (
                    <div className="flex justify-between">
                      <span className="text-gray-500">Bimini</span>
                      <span className="text-gray-900 font-medium">{item.bimini_type}</span>
                    </div>
                  )}
                  {item.canopy_carbon_poles && (
                    <div className="flex justify-between">
                      <span className="text-gray-500">Carbon Poles</span>
                      <span className="text-gray-900 font-medium">✓</span>
                    </div>
                  )}
                  {item.deck_material && (
                    <div className="flex justify-between">
                      <span className="text-gray-500">Ponte</span>
                      <span className="text-gray-900 font-medium">{item.deck_material}</span>
                    </div>
                  )}
                  {item.superstructure_material && (
                    <div className="flex justify-between">
                      <span className="text-gray-500">Soprastruttura</span>
                      <span className="text-gray-900 font-medium">{item.superstructure_material}</span>
                    </div>
                  )}
                  {item.stern_thruster && (
                    <div className="flex justify-between">
                      <span className="text-gray-500">Elica Prua</span>
                      <span className="text-gray-900 font-medium">✓ {item.stern_thruster_power_hp ? `(${item.stern_thruster_power_hp}HP)` : ''}</span>
                    </div>
                  )}
                  {item.stabilizing_fins && (
                    <div className="flex justify-between">
                      <span className="text-gray-500">Stabilizzatori</span>
                      <span className="text-gray-900 font-medium">✓ {item.fins_zero_speed ? '(Zero Speed)' : ''}</span>
                    </div>
                  )}
                  {item.underwater_lights && (
                    <div className="flex justify-between">
                      <span className="text-gray-500">Luci Sottozero</span>
                      <span className="text-gray-900 font-medium">✓</span>
                    </div>
                  )}
                  {item.cctv_cameras_count && (
                    <div className="flex justify-between">
                      <span className="text-gray-500">Telecamere CCTV</span>
                      <span className="text-gray-900 font-medium">{item.cctv_cameras_count}</span>
                    </div>
                  )}
                  {item.life_raft_capacity && (
                    <div className="flex justify-between">
                      <span className="text-gray-500">Zattera</span>
                      <span className="text-gray-900 font-medium">{item.life_raft_capacity} persone</span>
                    </div>
                  )}
                  {item.propeller_type && (
                    <div className="flex justify-between">
                      <span className="text-gray-500">Elica</span>
                      <span className="text-gray-900 font-medium">{item.propeller_type}</span>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* ===== SEPARATE SECTION: ELETTRONICA ===== */}
            {/* MAPPATURA NUOVA: tech = impianti + esterni + elettronica + optional */}
            {(activeSection === 'tech' || activeSection === 'info') && itemType === 'boats' && (
              <div className="bg-white rounded-xl p-3 border border-gray-200 shadow-sm" id="section-electronics">
                <h3 className="font-semibold text-gray-900 mb-2 text-xs flex items-center gap-2" style={{color: 'var(--color-primary, #242424)'}}><RadioLucideIcon className="w-4 h-4" /> Elettronica</h3>
                <div className="grid grid-cols-2 gap-x-3 gap-y-1 text-[10px]">
                  {item.radar_model && (
                    <div className="flex justify-between">
                      <span className="text-gray-500">Radar</span>
                      <span className="text-gray-900 font-medium">{item.radar_model}</span>
                    </div>
                  )}
                  {item.autopilot_model && (
                    <div className="flex justify-between">
                      <span className="text-gray-500">Pilota Automatico</span>
                      <span className="text-gray-900 font-medium">{item.autopilot_model}</span>
                    </div>
                  )}
                  {item.ais_system && (
                    <div className="flex justify-between">
                      <span className="text-gray-500">AIS</span>
                      <span className="text-gray-900 font-medium">✓</span>
                    </div>
                  )}
                  {item.plotter_screen_inches && (
                    <div className="flex justify-between">
                      <span className="text-gray-500">Plotter</span>
                      <span className="text-gray-900 font-medium">{item.plotter_screen_inches}"</span>
                    </div>
                  )}
                  {item.navigation_package && (
                    <div className="flex justify-between col-span-2">
                      <span className="text-gray-500">Pacchetto Navigazione</span>
                      <span className="text-gray-900 font-medium">{item.navigation_package}</span>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* ===== SEPARATE SECTION: INTRATTENIMENTO ===== */}
            {(activeSection === 'optionals' || activeSection === 'techdetails') && itemType === 'boats' && (
              <div className="bg-white rounded-xl p-3 border border-gray-200 shadow-sm" id="section-entertainment">
                <h3 className="font-semibold text-gray-900 mb-2 text-xs flex items-center gap-2" style={{color: 'var(--color-primary, #242424)'}}><TvLucideIcon className="w-4 h-4" /> Intrattenimento</h3>
                <div className="grid grid-cols-2 gap-x-3 gap-y-1 text-[10px]">
                  {item.tv_count && (
                    <div className="flex justify-between">
                      <span className="text-gray-500">TV</span>
                      <span className="text-gray-900 font-medium">{item.tv_count}</span>
                    </div>
                  )}
                  {item.audio_system && (
                    <div className="flex justify-between">
                      <span className="text-gray-500">Audio</span>
                      <span className="text-gray-900 font-medium">{item.audio_system}</span>
                    </div>
                  )}
                  {item.internet_package && (
                    <div className="flex justify-between">
                      <span className="text-gray-500">Internet</span>
                      <span className="text-gray-900 font-medium">{item.internet_package}</span>
                    </div>
                  )}
                  {item.sirius_system && (
                    <div className="flex justify-between">
                      <span className="text-gray-500">Sirius</span>
                      <span className="text-gray-900 font-medium">✓</span>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Optional Features - Dinamici dal database */}
            {(activeSection === 'optionals' || activeSection === 'all' || activeSection === 'info' || !isDesktopView) && (itemType === 'boats' || itemType === 'accessories') && item?.optional_features && item.optional_features.length > 0 && (
              <div className="space-y-3" id="section-optional">
                {/* Premium Features */}
                {item.optional_features.filter((o: any) => o.is_premium).length > 0 && (
                  <div className="bg-purple-50 rounded-xl p-3 border border-purple-200">
                    <h3 className="font-semibold text-purple-900 mb-2 text-xs flex items-center gap-2">
                      <StarLucideIcon className="w-4 h-4" /> Premium ({item.optional_features.filter((o: any) => o.is_premium).length})
                    </h3>
                    <div className="flex flex-wrap gap-1">
                      {item.optional_features.filter((o: any) => o.is_premium).map((opt: any, idx: number) => (
                        <span key={`${opt.name}-${idx}`} className="px-2 py-0.5 bg-purple-100 text-purple-800 rounded text-[9px] font-medium">
                          {opt.name}
                          {opt.added_price > 0 && <span className="ml-1 text-purple-600">+€{opt.added_price.toLocaleString('it-IT')}</span>}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
                
                {/* Dynamic Sections by Category */}
                {Object.entries(categoryConfig).map(([category, config]) => (
                  <DynamicSection
                    key={category}
                    category={category}
                    features={item?.optional_features}
                    icon={config.icon}
                    title={config.label}
                    color={config.color}
                    showPrices={showPrices}
                    is_new_boat={item.is_new_boat}
                  />
                ))}
              </div>
            )}

            {/* ===== SEPARATE SECTION: ACCESSORI ===== */}
            {(activeSection === 'accessories' || activeSection === 'all' || activeSection === 'info' || !isDesktopView) && itemType === 'accessories' && (
              <div className="space-y-4" id="section-accessories">
                {/* Basic Info */}
                {(item.brand || item.model || item.year || item.category) && (
                  <div className="bg-white rounded-xl p-3 border border-gray-200 shadow-sm">
                    <h3 className="font-semibold text-gray-900 mb-2 text-xs flex items-center gap-2" style={{color: 'var(--color-primary, #242424)'}}><PackageLucideIcon className="w-4 h-4" /> Dettagli Accessorio</h3>
                    <div className="grid grid-cols-2 gap-x-3 gap-y-1 text-[10px]">
                      {item.brand && (
                        <div className="flex justify-between"><span className="text-gray-500">Marca</span><span className="text-gray-900 font-medium">{item.brand}</span></div>
                      )}
                      {item.model && (
                        <div className="flex justify-between"><span className="text-gray-500">Modello</span><span className="text-gray-900 font-medium">{item.model}</span></div>
                      )}
                      {item.year && (
                        <div className="flex justify-between"><span className="text-gray-500">Anno</span><span className="text-gray-900 font-medium">{item.year}</span></div>
                      )}
                      {item.category && (
                        <div className="flex justify-between"><span className="text-gray-500">Categoria</span><span className="text-gray-900 font-medium">{item.category}</span></div>
                      )}
                      {item.compatibility && (
                        <div className="flex justify-between col-span-2"><span className="text-gray-500">Compatibilità</span><span className="text-gray-900 font-medium">{item.compatibility}</span></div>
                      )}
                      {item.brand_compatibility && (
                        <div className="flex justify-between col-span-2"><span className="text-gray-500">Compatibile con</span><span className="text-gray-900 font-medium">{item.brand_compatibility}</span></div>
                      )}
                    </div>
                  </div>
                )}

                {/* Condition */}
                {(item.condition || item.usage_hours || item.wear_level) && (
                  <div className="bg-white rounded-xl p-3 border border-gray-200 shadow-sm">
                    <h3 className="font-semibold text-gray-900 mb-2 text-xs flex items-center gap-2" style={{color: 'var(--color-primary, #242424)'}}><ShieldLucideIcon className="w-4 h-4" /> Condizione</h3>
                    <div className="grid grid-cols-2 gap-x-3 gap-y-1 text-[10px]">
                      {item.condition && (
                        <div className="flex justify-between"><span className="text-gray-500">Condizione</span><span className="text-gray-900 font-medium">{item.condition}</span></div>
                      )}
                      {item.usage_hours !== undefined && item.usage_hours !== null && (
                        <div className="flex justify-between"><span className="text-gray-500">Ore Uso</span><span className="text-gray-900 font-medium">{item.usage_hours}h</span></div>
                      )}
                      {item.wear_level && (
                        <div className="flex justify-between"><span className="text-gray-500">Usura</span><span className="text-gray-900 font-medium">{item.wear_level}</span></div>
                      )}
                    </div>
                  </div>
                )}

                {/* Optional Features per accessori (ex Specifiche Tecniche) */}
                {item.optional_features && item.optional_features.length > 0 && (
                  <div className="bg-white rounded-xl p-3 border border-gray-200 shadow-sm">
                    <h3 className="font-semibold text-gray-900 mb-2 text-xs flex items-center gap-2" style={{color: 'var(--color-primary, #242424)'}}><StarLucideIcon className="w-4 h-4" /> Caratteristiche</h3>
                    <div className="grid grid-cols-2 gap-x-3 gap-y-1 text-[10px]">
                      {item.optional_features.map((opt: any, idx: number) => (
                        <div key={`${opt.name}-${idx}`} className="flex justify-between">
                          <span className="text-gray-500">{opt.name}</span>
                          <span className="text-gray-900 font-medium">
                            {opt.value || (opt.added_price > 0 ? `+€${opt.added_price.toLocaleString('it-IT')}` : '✓')}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* ===== SEPARATE SECTION: DETTAGLI TECNICI COMPLETI ===== */}
            {/* Show all specs grouped by category - navigable via 'techdetails' tab */}
            {(activeSection === 'techdetails' || activeSection === 'all' || activeSection === 'specs' || !isDesktopView) && itemType === 'boats' && (
              <div className="space-y-4" id="section-tech-details">
                {/* Dati Barca */}
                {(item.length_m || item.beam_m || item.draft_m || item.cabins_count || item.bathrooms_count || item.boat_type || item.boat_type_code || item.hull_material) && (
                  <div className="bg-white rounded-xl p-3 border border-gray-200 shadow-sm">
                    <h3 className="font-semibold text-gray-900 mb-2 text-xs flex items-center gap-2" style={{color: 'var(--color-primary, #242424)'}}><ShipIcon className="w-4 h-4" /> Dati Barca</h3>
                    <div className="grid grid-cols-2 gap-x-3 gap-y-1 text-[10px]">
                      {item.length_m && (
                        <div className="flex justify-between"><span className="text-gray-500">Lunghezza</span><span className="text-gray-900 font-medium">{item.length_m}m</span></div>
                      )}
                      {item.beam_m && (
                        <div className="flex justify-between"><span className="text-gray-500">Larghezza</span><span className="text-gray-900 font-medium">{item.beam_m}m</span></div>
                      )}
                      {item.draft_m && (
                        <div className="flex justify-between"><span className="text-gray-500">Pescaggio</span><span className="text-gray-900 font-medium">{item.draft_m}m</span></div>
                      )}
                      {item.cabins_count && (
                        <div className="flex justify-between"><span className="text-gray-500">Cabine</span><span className="text-gray-900 font-medium">{item.cabins_count}</span></div>
                      )}
                      {item.bathrooms_count && (
                        <div className="flex justify-between"><span className="text-gray-500">Bagni</span><span className="text-gray-900 font-medium">{item.bathrooms_count}</span></div>
                      )}
                      {item.boat_type && (
                        <div className="flex justify-between"><span className="text-gray-500">Tipo</span><span className="text-gray-900 font-medium">{item.boat_type}</span></div>
                      )}
                      {item.boat_type_code && (
                        <div className="flex justify-between"><span className="text-gray-500">Tipologia</span><span className="text-gray-900 font-medium">{BOAT_TYPES.find(t => t.code === item.boat_type_code)?.nome || item.boat_type_code}</span></div>
                      )}
                      {item.hull_material && (
                        <div className="flex justify-between"><span className="text-gray-500">Scafo</span><span className="text-gray-900 font-medium">{item.hull_material}</span></div>
                      )}
                      {item.cabin_layout && (
                        <div className="flex justify-between"><span className="text-gray-500">Layout</span><span className="text-gray-900 font-medium">{item.cabin_layout}</span></div>
                      )}
                    </div>
                  </div>
                )}

                {/* Motori */}
                {(item.engines_brand || item.engines_count || item.engines_power_hp || item.transmission_type || item.max_speed_knots || item.propeller_type) && (
                  <div className="bg-white rounded-xl p-3 border border-gray-200 shadow-sm">
                    <h3 className="font-semibold text-gray-900 mb-2 text-xs flex items-center gap-2" style={{color: 'var(--color-primary, #242424)'}}><SettingsLucideIcon className="w-4 h-4" /> Motori</h3>
                    <div className="grid grid-cols-2 gap-x-3 gap-y-1 text-[10px]">
                      {item.engines_brand && (
                        <div className="flex justify-between"><span className="text-gray-500">Motore</span><span className="text-gray-900 font-medium">{item.engines_brand} {item.engines_model}</span></div>
                      )}
                      {item.engines_count && (
                        <div className="flex justify-between"><span className="text-gray-500">Numero</span><span className="text-gray-900 font-medium">{item.engines_count}</span></div>
                      )}
                      {item.engines_power_hp && (
                        <div className="flex justify-between"><span className="text-gray-500">Potenza</span><span className="text-gray-900 font-medium">{item.engines_power_hp} HP</span></div>
                      )}
                      {item.engines_hours && (
                        <div className="flex justify-between"><span className="text-gray-500">Ore</span><span className="text-gray-900 font-medium">{item.engines_hours}</span></div>
                      )}
                      {item.transmission_type && (
                        <div className="flex justify-between"><span className="text-gray-500">Trasmissione</span><span className="text-gray-900 font-medium">{item.transmission_type}</span></div>
                      )}
                      {item.max_speed_knots && (
                        <div className="flex justify-between"><span className="text-gray-500">Vel. Max</span><span className="text-gray-900 font-medium">{item.max_speed_knots} nodi</span></div>
                      )}
                      {item.cruising_speed_knots && (
                        <div className="flex justify-between"><span className="text-gray-500">Crociera</span><span className="text-gray-900 font-medium">{item.cruising_speed_knots} nodi</span></div>
                      )}
                      {item.range_nm && (
                        <div className="flex justify-between"><span className="text-gray-500">Autonomia</span><span className="text-gray-900 font-medium">{item.range_nm} nm</span></div>
                      )}
                      {item.propeller_type && (
                        <div className="flex justify-between"><span className="text-gray-500">Elica</span><span className="text-gray-900 font-medium">{item.propeller_type}</span></div>
                      )}
                    </div>
                  </div>
                )}

                {/* Interni */}
                {(item.interior_style || item.flooring_type || item.upholstery_material) && (
                  <div className="bg-white rounded-xl p-3 border border-gray-200 shadow-sm">
                    <h3 className="font-semibold text-gray-900 mb-2 text-xs flex items-center gap-2" style={{color: 'var(--color-primary, #242424)'}}><HomeLucideIcon className="w-4 h-4" /> Interni</h3>
                    <div className="grid grid-cols-2 gap-x-3 gap-y-1 text-[10px]">
                      {item.interior_style && (
                        <div className="flex justify-between"><span className="text-gray-500">Stile</span><span className="text-gray-900 font-medium">{item.interior_style}</span></div>
                      )}
                      {item.flooring_type && (
                        <div className="flex justify-between"><span className="text-gray-500">Pavimentazione</span><span className="text-gray-900 font-medium">{item.flooring_type}</span></div>
                      )}
                      {item.upholstery_material && (
                        <div className="flex justify-between"><span className="text-gray-500">Tappezzeria</span><span className="text-gray-900 font-medium">{item.upholstery_material}</span></div>
                      )}
                    </div>
                  </div>
                )}

                {/* Comfort */}
                {(item.air_conditioning || item.heating || item.galley_brand || item.ice_maker || item.grill_type) && (
                  <div className="bg-white rounded-xl p-3 border border-gray-200 shadow-sm">
                    <h3 className="font-semibold text-gray-900 mb-2 text-xs flex items-center gap-2" style={{color: 'var(--color-primary, #242424)'}}><ArmchairLucideIcon className="w-4 h-4" /> Comfort</h3>
                    <div className="grid grid-cols-2 gap-x-3 gap-y-1 text-[10px]">
                      {item.air_conditioning && (
                        <div className="flex justify-between"><span className="text-gray-500">Aria Condiz.</span><span className="text-gray-900 font-medium">✓</span></div>
                      )}
                      {item.heating && (
                        <div className="flex justify-between"><span className="text-gray-500">Riscaldamento</span><span className="text-gray-900 font-medium">✓</span></div>
                      )}
                      {item.galley_brand && (
                        <div className="flex justify-between"><span className="text-gray-500">Cucina</span><span className="text-gray-900 font-medium">{item.galley_brand}</span></div>
                      )}
                      {item.ice_maker && (
                        <div className="flex justify-between"><span className="text-gray-500">Ice Maker</span><span className="text-gray-900 font-medium">✓</span></div>
                      )}
                      {item.grill_type && (
                        <div className="flex justify-between"><span className="text-gray-500">Grill</span><span className="text-gray-900 font-medium">{item.grill_type}</span></div>
                      )}
                    </div>
                  </div>
                )}

                {/* Sicurezza */}
                {(item.life_raft_capacity || item.cctv_cameras_count || item.underwater_lights || item.stern_thruster || item.stabilizing_fins) && (
                  <div className="bg-white rounded-xl p-3 border border-gray-200 shadow-sm">
                    <h3 className="font-semibold text-gray-900 mb-2 text-xs flex items-center gap-2" style={{color: 'var(--color-primary, #242424)'}}><ShieldLucideIcon className="w-4 h-4" /> Sicurezza</h3>
                    <div className="grid grid-cols-2 gap-x-3 gap-y-1 text-[10px]">
                      {item.stern_thruster && (
                        <div className="flex justify-between"><span className="text-gray-500">Elica Prua</span><span className="text-gray-900 font-medium">✓ {item.stern_thruster_power_hp ? `(${item.stern_thruster_power_hp}HP)` : ''}</span></div>
                      )}
                      {item.stabilizing_fins && (
                        <div className="flex justify-between"><span className="text-gray-500">Stabilizzatori</span><span className="text-gray-900 font-medium">✓ {item.fins_zero_speed ? '(Zero Speed)' : ''}</span></div>
                      )}
                      {item.underwater_lights && (
                        <div className="flex justify-between"><span className="text-gray-500">Luci Sottozero</span><span className="text-gray-900 font-medium">✓</span></div>
                      )}
                      {item.cctv_cameras_count && (
                        <div className="flex justify-between"><span className="text-gray-500">Telecamere</span><span className="text-gray-900 font-medium">{item.cctv_cameras_count}</span></div>
                      )}
                      {item.life_raft_capacity && (
                        <div className="flex justify-between"><span className="text-gray-500">Zattera</span><span className="text-gray-900 font-medium">{item.life_raft_capacity} pax</span></div>
                      )}
                    </div>
                  </div>
                )}

                {/* Impianti */}
                {(item.fuel_capacity_liters || item.water_capacity_liters || item.generator_brand || item.voltage_config || item.batteries_count || item.water_maker || item.autoclave) && (
                  <div className="bg-white rounded-xl p-3 border border-gray-200 shadow-sm">
                    <h3 className="font-semibold text-gray-900 mb-2 text-xs flex items-center gap-2" style={{color: 'var(--color-primary, #242424)'}}><FuelLucideIcon className="w-4 h-4" /> Impianti</h3>
                    <div className="grid grid-cols-2 gap-x-3 gap-y-1 text-[10px]">
                      {item.fuel_capacity_liters && (
                        <div className="flex justify-between"><span className="text-gray-500">Serbatoio Carburante</span><span className="text-gray-900 font-medium">{item.fuel_capacity_liters}L</span></div>
                      )}
                      {item.water_capacity_liters && (
                        <div className="flex justify-between"><span className="text-gray-500">Serbatoio Acqua</span><span className="text-gray-900 font-medium">{item.water_capacity_liters}L</span></div>
                      )}
                      {item.voltage_config && (
                        <div className="flex justify-between"><span className="text-gray-500">Voltaggio</span><span className="text-gray-900 font-medium">{item.voltage_config}</span></div>
                      )}
                      {item.generator_brand && (
                        <div className="flex justify-between"><span className="text-gray-500">Generatore</span><span className="text-gray-900 font-medium">{item.generator_brand} {item.generator_power_kw}kW</span></div>
                      )}
                      {item.batteries_count && (
                        <div className="flex justify-between"><span className="text-gray-500">Batterie</span><span className="text-gray-900 font-medium">{item.batteries_count}x {item.batteries_capacity_ah}Ah</span></div>
                      )}
                      {item.water_maker && (
                        <div className="flex justify-between"><span className="text-gray-500">Dissalatore</span><span className="text-gray-900 font-medium">✓</span></div>
                      )}
                      {item.autoclave && (
                        <div className="flex justify-between"><span className="text-gray-500">Autoclave</span><span className="text-gray-900 font-medium">✓</span></div>
                      )}
                      {item.boiler_present && item.boiler_type && (
                        <div className="flex justify-between"><span className="text-gray-500">Boiler</span><span className="text-gray-900 font-medium">{item.boiler_type}</span></div>
                      )}
                    </div>
                  </div>
                )}

                {/* Esterni */}
                {(item.teak_deck || item.flybridge || item.bimini_type || item.deck_material || item.superstructure_material) && (
                  <div className="bg-white rounded-xl p-3 border border-gray-200 shadow-sm">
                    <h3 className="font-semibold text-gray-900 mb-2 text-xs flex items-center gap-2" style={{color: 'var(--color-primary, #242424)'}}><SunLucideIcon className="w-4 h-4" /> Esterni</h3>
                    <div className="grid grid-cols-2 gap-x-3 gap-y-1 text-[10px]">
                      {item.teak_deck && (
                        <div className="flex justify-between"><span className="text-gray-500">Ponte Teak</span><span className="text-gray-900 font-medium">✓</span></div>
                      )}
                      {item.flybridge && (
                        <div className="flex justify-between"><span className="text-gray-500">Flybridge</span><span className="text-gray-900 font-medium">✓</span></div>
                      )}
                      {item.bimini_type && (
                        <div className="flex justify-between"><span className="text-gray-500">Bimini</span><span className="text-gray-900 font-medium">{item.bimini_type}</span></div>
                      )}
                      {item.deck_material && (
                        <div className="flex justify-between"><span className="text-gray-500">Ponte</span><span className="text-gray-900 font-medium">{item.deck_material}</span></div>
                      )}
                      {item.superstructure_material && (
                        <div className="flex justify-between"><span className="text-gray-500">Soprastruttura</span><span className="text-gray-900 font-medium">{item.superstructure_material}</span></div>
                      )}
                      {item.teak_caulking_color && (
                        <div className="flex justify-between"><span className="text-gray-500">Teak Color</span><span className="text-gray-900 font-medium">{item.teak_caulking_color}</span></div>
                      )}
                    </div>
                  </div>
                )}

                {/* Elettronica */}
                {(item.radar_model || item.autopilot_model || item.ais_system || item.plotter_screen_inches || item.navigation_package) && (
                  <div className="bg-white rounded-xl p-3 border border-gray-200 shadow-sm">
                    <h3 className="font-semibold text-gray-900 mb-2 text-xs flex items-center gap-2" style={{color: 'var(--color-primary, #242424)'}}><RadioLucideIcon className="w-4 h-4" /> Elettronica</h3>
                    <div className="grid grid-cols-2 gap-x-3 gap-y-1 text-[10px]">
                      {item.radar_model && (
                        <div className="flex justify-between"><span className="text-gray-500">Radar</span><span className="text-gray-900 font-medium">{item.radar_model}</span></div>
                      )}
                      {item.autopilot_model && (
                        <div className="flex justify-between"><span className="text-gray-500">Pilota Auto.</span><span className="text-gray-900 font-medium">{item.autopilot_model}</span></div>
                      )}
                      {item.ais_system && (
                        <div className="flex justify-between"><span className="text-gray-500">AIS</span><span className="text-gray-900 font-medium">✓</span></div>
                      )}
                      {item.plotter_screen_inches && (
                        <div className="flex justify-between"><span className="text-gray-500">Plotter</span><span className="text-gray-900 font-medium">{item.plotter_screen_inches}"</span></div>
                      )}
                      {item.navigation_package && (
                        <div className="flex justify-between col-span-2"><span className="text-gray-500">Pacchetto Nav.</span><span className="text-gray-900 font-medium">{item.navigation_package}</span></div>
                      )}
                    </div>
                  </div>
                )}

                {/* Navigazione */}
                {(item.gps_model || item.vhf_model || item.sounder_model) && (
                  <div className="bg-white rounded-xl p-3 border border-gray-200 shadow-sm">
                    <h3 className="font-semibold text-gray-900 mb-2 text-xs flex items-center gap-2" style={{color: 'var(--color-primary, #242424)'}}><CompassLucideIcon className="w-4 h-4" /> Navigazione</h3>
                    <div className="grid grid-cols-2 gap-x-3 gap-y-1 text-[10px]">
                      {item.gps_model && (
                        <div className="flex justify-between"><span className="text-gray-500">GPS</span><span className="text-gray-900 font-medium">{item.gps_model}</span></div>
                      )}
                      {item.vhf_model && (
                        <div className="flex justify-between"><span className="text-gray-500">VHF</span><span className="text-gray-900 font-medium">{item.vhf_model}</span></div>
                      )}
                      {item.sounder_model && (
                        <div className="flex justify-between"><span className="text-gray-500">Ecoscandaglio</span><span className="text-gray-900 font-medium">{item.sounder_model}</span></div>
                      )}
                    </div>
                  </div>
                )}

                {/* Intrattenimento */}
                {(item.tv_count || item.audio_system || item.internet_package || item.sirius_system) && (
                  <div className="bg-white rounded-xl p-3 border border-gray-200 shadow-sm">
                    <h3 className="font-semibold text-gray-900 mb-2 text-xs flex items-center gap-2" style={{color: 'var(--color-primary, #242424)'}}><TvLucideIcon className="w-4 h-4" /> Intrattenimento</h3>
                    <div className="grid grid-cols-2 gap-x-3 gap-y-1 text-[10px]">
                      {item.tv_count && (
                        <div className="flex justify-between"><span className="text-gray-500">TV</span><span className="text-gray-900 font-medium">{item.tv_count}</span></div>
                      )}
                      {item.audio_system && (
                        <div className="flex justify-between"><span className="text-gray-500">Audio</span><span className="text-gray-900 font-medium">{item.audio_system}</span></div>
                      )}
                      {item.internet_package && (
                        <div className="flex justify-between"><span className="text-gray-500">Internet</span><span className="text-gray-900 font-medium">{item.internet_package}</span></div>
                      )}
                      {item.sirius_system && (
                        <div className="flex justify-between"><span className="text-gray-500">Sirius</span><span className="text-gray-900 font-medium">✓</span></div>
                      )}
                    </div>
                  </div>
                )}

                {/* Specifiche Tipologia */}
                {item.boat_type_code && item.technical_specs && (() => {
                  const specs = getTechnicalSpecs(item.technical_specs, item.boat_type_code);
                  const boatTypeInfo = BOAT_TYPES.find(t => t.code === item.boat_type_code);
                  if (specs && specs.length > 0) {
                    return (
                      <div className="bg-white rounded-xl p-3 border border-gray-200 shadow-sm">
                        <h3 className="font-semibold text-gray-900 mb-2 text-xs flex items-center gap-2" style={{color: 'var(--color-primary, #242424)'}}><ShipIcon className="w-4 h-4" /> Specifiche {boatTypeInfo?.nome || item.boat_type_code}</h3>
                        <div className="grid grid-cols-2 gap-x-3 gap-y-1 text-[10px]">
                          {specs.map((spec, idx) => (
                            <div key={`${spec.label}-${idx}`} className="flex justify-between">
                              <span className="text-gray-500">{spec.label}</span>
                              <span className="text-gray-900 font-medium">{String(spec.value)}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    );
                  }
                  return null;
                })()}
              </div>
            )}
          </div>
        </div>

        {/* Colonna 3 (12%) - Azioni */}
        <div className="desktop-col-actions w-[12%] min-w-[140px] bg-white p-2 border-l border-gray-200">
          <div className="space-y-2 sticky top-0">
            <h3 className="font-semibold text-gray-900 text-sm mb-2">Azioni</h3>
            
            {/* Reservation Badge */}
            {item.has_active_reservation && (
              <div className="mb-4">
                <ReservationBadge
                  expiresAt={item.reservation.expires_at}
                  dealerName={item.reservation.dealer_name}
                  isMyReservation={isAuthenticated && user?.id === item.reservation.dealer_id}
                />
              </div>
            )}
            
            {/* Action Buttons - Always visible */}
            <div className="flex flex-col gap-2">
              {isAuthenticated && !isOwnItem && user && (
                <ReservationButton
                  itemId={item.id}
                  itemType={itemType || 'boats'}
                  itemStatus={item.status}
                  reservation={item.reservation}
                  currentUserId={user.id}
                  onReservationChange={() => refetch()}
                  trackClick={trackClick}
                  item={item}
                />
              )}
              
              {isAuthenticated && !isOwnItem && (user?.is_admin || ((item.status === 'available' || item.status === 'published') && !item.has_active_reservation)) && (
                <button
                  onClick={() => { handleTrackClick('quote'); setShowTradeModal(true); }}
                  className="btn-primary flex items-center justify-center gap-1 w-full text-xs py-2"
                >
                  <MessageSquareLucideIcon className="h-3 w-3" />
                  Trattativa
                </button>
              )}
              
              <button
                onClick={() => {
                  handleTrackClick('contact');
                  if (!isAuthenticated) {
                    toast.error('Effettua il login per contattare');
                    navigate('/login');
                    return;
                  }
                  navigate(`/${itemType}/${item.id}/chat`);
                }}
                className="btn-secondary flex items-center justify-center gap-1 w-full text-xs py-2"
              >
                <MessageSquareLucideIcon className="h-3 w-3" />
                Contatta
              </button>
              
              {!isOwnItem && (
                <button
                  onClick={() => { handleTrackClick('share'); setShowShareModal(true); }}
                  className="btn-secondary flex items-center justify-center gap-1 w-full text-xs py-2 bg-green-600 text-white hover:bg-green-700 border-green-600"
                >
                  <ShareLucideIcon className="h-3 w-3" />
                  Condividi
                </button>
              )}

              {/* Owner/Admin Actions */}
              {isAuthenticated && (user?.is_admin || isOwnItem) && item?.status === 'available' && (
                <div className="pt-2 border-t border-gray-200 mt-2">
                  <button
                    onClick={() => setShowMarkSoldModal(true)}
                    className="btn-primary flex items-center justify-center gap-1 w-full text-xs py-2 bg-green-600 hover:bg-green-700 border-green-600"
                  >
                    <CheckCircleLucideIcon className="h-3 w-3" />
                    Venduto
                  </button>
                </div>
              )}

              {/* Edit/Delete for owner */}
              {isAuthenticated && (user?.is_admin || isOwnItem) && (
                <div className="pt-2 border-t border-gray-200 mt-2">
                  <button
                    onClick={() => navigate(`/edit-${itemType}/${item.id}`)}
                    className="btn-secondary flex items-center justify-center gap-1 w-full text-xs py-2"
                  >
                    <EditLucideIcon className="h-4 w-4" />
                    Modifica
                  </button>
                  {user?.is_admin && (
                    <button
                      onClick={() => {
                        if (confirm('Sei sicuro di voler eliminare questa barca?')) {
                          api.delete(`/${itemType}/${item.id}`)
                            .then(() => {
                              toast.success('Elemento eliminato');
                              navigate(`/${itemType}`);
                            })
                            .catch(() => {
                              toast.error('Errore durante l\'eliminazione');
                            });
                        }
                      }}
                      className="btn-secondary flex items-center justify-center gap-2 w-full text-red-600 hover:text-red-700 border-red-300 hover:border-red-400 mt-2"
                    >
                      <TrashLucideIcon className="h-4 w-4" />
                      Elimina
                    </button>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Trade Modal */}
        <IOSheet
          isOpen={showTradeModal}
          onClose={() => setShowTradeModal(false)}
          title="Proponi Trattativa"
          snapPoints={[0.5, 0.9]}
        >
          <div className="p-4">
            <form onSubmit={handleTradeSubmit}>
              <div className="mb-4">
                <label className="block text-sm font-medium mb-1">Tua offerta (€)</label>
                <input
                  type="number"
                  value={tradePrice}
                  onChange={(e) => setTradePrice(e.target.value)}
                  className="input-field"
                  required
                />
              </div>
              
              <div className="mb-4">
                <label className="block text-sm font-medium mb-1">Messaggio</label>
                <textarea
                  value={tradeMessage}
                  onChange={(e) => setTradeMessage(e.target.value)}
                  className="input-field"
                  rows={3}
                  placeholder="Scrivi un messaggio al venditore..."
                />
              </div>
              
              <div className="flex gap-3">
                <button
                  type="button"
                  onClick={() => setShowTradeModal(false)}
                  className="flex-1 btn-secondary"
                >
                  Annulla
                </button>
                <button
                  type="submit"
                  disabled={tradeMutation.isLoading}
                  className="flex-1 btn-primary"
                >
                  {tradeMutation.isLoading ? 'Invio...' : 'Invia'}
                </button>
              </div>
            </form>
          </div>
        </IOSheet>

        {/* Share Modal */}
        <IOSheet
          isOpen={showShareModal}
          onClose={() => setShowShareModal(false)}
          title="Condividi"
          snapPoints={[0.5, 0.9]}
        >
          <div className="p-4">
            {!generatedLink ? (
              <form onSubmit={(e) => {
                e.preventDefault();
                shareMutation.mutate({ expires_hours: shareHours });
              }}>
                <div className="mb-4">
                  <label className="block text-sm font-medium mb-1">Valido per</label>
                  <select
                    value={shareHours}
                    onChange={(e) => setShareHours(parseInt(e.target.value))}
                    className="input-field"
                  >
                    <option value={24}>24 ore</option>
                    <option value={48}>48 ore</option>
                    <option value={72}>72 ore</option>
                    <option value={168}>1 settimana</option>
                  </select>
                </div>
                
                <button
                  type="submit"
                  disabled={shareMutation.isLoading}
                  className="w-full btn-primary"
                >
                  {shareMutation.isLoading ? 'Creazione...' : 'Crea Link'}
                </button>
              </form>
            ) : (
              <div className="space-y-4">
                <div className="bg-green-50 border border-green-200 rounded-lg p-3">
                  <p className="text-sm text-green-800">✅ Link creato!</p>
                  <p className="text-xs text-green-600">Valido per {shareHours} ore</p>
                </div>
                
                <input
                  type="text"
                  value={generatedLink}
                  readOnly
                  className="input-field text-sm"
                  onClick={(e) => (e.target as HTMLInputElement).select()}
                />
                
                <div className="flex gap-3">
                  <button
                    onClick={shareWhatsApp}
                    className="flex-1 bg-green-500 text-white py-3 rounded-lg font-medium hover:bg-green-600 flex items-center justify-center gap-2"
                  >
                    <ShareLucideIcon className="w-4 h-4" />
                    WhatsApp
                  </button>
                  <button
                    onClick={() => {
                      navigator.clipboard.writeText(generatedLink);
                      toast.success('Copiato!');
                    }}
                    className="flex-1 btn-secondary"
                  >
                    Copia Link
                  </button>
                </div>
                
                <button
                  onClick={() => {
                    setGeneratedLink('');
                    setShareData(null);
                  }}
                  className="w-full text-sm text-gray-500 hover:text-gray-700"
                >
                  Crea nuovo link
                </button>
              </div>
            )}
          </div>
        </IOSheet>
        
        {/* Mark Sold Confirmation Modal */}
        <IOSheet
          isOpen={showMarkSoldModal}
          onClose={() => setShowMarkSoldModal(false)}
          title="Conferma Vendita"
          snapPoints={[0.4]}
        >
          <div className="p-4 space-y-4">
            <div className="text-center">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <CheckCircleLucideIcon className="w-8 h-8 text-green-600" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">
                Segna come Venduto
              </h3>
              <p className="text-gray-600">
                Stai per segnare questo {config.singular.toLowerCase()} come <strong>venduto</strong>.
                Questa azione può essere annullata solo da un amministratore.
              </p>
            </div>
            
            <div className="flex gap-3 pt-4">
              <button
                onClick={() => setShowMarkSoldModal(false)}
                className="flex-1 btn-secondary"
                disabled={markSoldMutation.isLoading}
              >
                Annulla
              </button>
              <button
                onClick={() => markSoldMutation.mutate()}
                disabled={markSoldMutation.isLoading}
                className="flex-1 btn-primary bg-green-600 hover:bg-green-700 border-green-600"
              >
                {markSoldMutation.isLoading ? 'Conferma...' : 'Conferma Vendita'}
              </button>
            </div>
          </div>
        </IOSheet>
      </div>
    );
  }

  // Mobile/PWA layout - iOS Native Design
  return (
    <div className="ios-page" data-testid="mobile-item-detail">
      {/* Scrollable Content */}
      <div className="px-3 pt-0 pb-4">
        {/* Gallery - full width with proper touch handling */}
        <div className="-mx-3 mb-1" style={{ touchAction: 'pan-y' }}>
          <ItemGallery 
            images={images} 
            videos={videos}
            title={item.title}
            distance={itemDistance ?? undefined}
            status={item.status}
            reservation={item.reservation}
            itemData={item}
          />
        </div>

        {/* Dealer + Price Card */}
        <div className="ios-card mb-3">
          {/* Dealer Row */}
          {(item.dealer_name || item.dealer_logo) && (
            <div className="ios-list-row gap-3">
              {item.dealer_logo ? (
                <img src={fixImageUrl(item.dealer_logo)} alt={item.dealer_name} width={160} height={160} className="w-[160px] h-[160px] rounded-lg object-cover" />
              ) : (
                <div className="w-10 h-10 rounded-lg bg-[var(--ios-blue)]/10 flex items-center justify-center flex-shrink-0">
                  {itemType === 'boats' ? <AnchorLucideIcon className="w-5 h-5 text-[var(--ios-blue)]" /> : <ZapLucideIcon className="w-5 h-5 text-[var(--ios-blue)]" />}
                </div>
              )}
              <div className="flex-1 min-w-0">
                <p className="text-[11px] font-semibold uppercase tracking-wide text-[var(--ios-blue)]">{sellerLabel}</p>
                <p className="text-[15px] font-semibold text-[var(--ios-text-primary)] truncate">{item.dealer_name}</p>
                {item.location && (
                  <p className="text-[12px] text-[var(--ios-text-tertiary)] flex items-center gap-1">
                    <MapPinLucideIcon className="w-3 h-3" />{item.location}
                  </p>
                )}
              </div>
              {itemDistance !== null && (
                <div className="flex flex-col items-center px-3 py-1.5 bg-[var(--ios-blue)]/5 rounded-lg min-w-[70px]">
                  <span className="text-[13px] font-bold text-[var(--ios-blue)]">{itemDistance}</span>
                  <span className="text-[9px] text-[var(--ios-text-tertiary)] uppercase">km</span>
                </div>
              )}
            </div>
          )}

          {/* Price Section */}
          <div className="px-4 pb-3">
            <h1 className="text-[17px] font-semibold text-[var(--ios-text-primary)] mb-1">{item.title}</h1>
            {item.reservation && (
              <div className="mb-2">
                <ReservationBadge
                  expiresAt={item.reservation.expires_at}
                  dealerName={item.reservation.dealer_name}
                  isMyReservation={isAuthenticated && user?.id === item.reservation.dealer_id}
                />
              </div>
            )}

            {item.is_new_boat === true ? (
              <div className="bg-green-50 rounded-lg p-3 border border-green-200 mt-1">
                <p className="text-[10px] text-green-700 font-medium mb-0.5">Prezzo Configurazione</p>
                <p className="text-[20px] font-bold text-green-700">€{configurationPrice()?.toLocaleString('it-IT')}</p>
                <p className="text-[10px] text-green-600 mt-0.5">€{item.retail_price?.toLocaleString('it-IT')} base + €{totalOptional().toLocaleString('it-IT')} optional</p>
              </div>
            ) : isPublicView ? (
              <div className="mt-1">
                <p className="text-[11px] text-[var(--ios-text-tertiary)]">Prezzo al Pubblico</p>
                <p className="text-[20px] font-bold text-[var(--ios-blue)]">{formatPrice(item.retail_price || item.dealer_price)}</p>
              </div>
            ) : (
              <div className="mt-1">
                {showPrices ? (
                  <>
                    <div className="flex items-center justify-between">
                      <p className="text-[11px] text-[var(--ios-text-tertiary)]">{priceLabel}</p>
                      <button onClick={() => setShowPrices(!showPrices)} className="p-1 active:opacity-50"><EyeOffLucideIcon className="w-4 h-4 text-[var(--ios-text-tertiary)]" /></button>
                    </div>
                    <p className="text-[20px] font-bold text-[var(--ios-blue)]">{formatPrice(item.dealer_price)}</p>
                    {item.retail_price && (
                      <p className="text-[12px] text-[var(--ios-text-tertiary)]">Pubblico: €{item.retail_price.toLocaleString('it-IT')}</p>
                    )}
                    {(() => {
                      const pct = calculateProfitPercentage(item.dealer_price, item.retail_price);
                      return pct ? <p className="text-[11px] font-semibold text-green-600">+{pct}% guadagno</p> : null;
                    })()}
                  </>
                ) : (
                  <button onClick={() => setShowPrices(!showPrices)} className="flex items-center gap-1 text-[13px] text-[var(--ios-blue)]"><EyeLucideIcon className="w-4 h-4" /> Mostra prezzi</button>
                )}
              </div>
            )}
          </div>

          {/* Market Price Benchmark - Mobile/PWA */}
          {itemType === 'boats' && item.is_new_boat !== true && (
            <div className="px-4 pb-3">
              <MarketPrice boatId={item.id} price={item.dealer_price || item.retail_price || undefined} />
            </div>
          )}

          {/* Quote Request for unauthenticated users */}
          {!isAuthenticated && (
            <div className="px-4 pb-3">
              <QuoteRequestButton 
                itemId={item.id} 
                itemType={itemType === 'boats' ? 'boat' : itemType === 'motors' ? 'motor' : 'accessory'}
                brand={item.brand}
                model={item.model}
              />
            </div>
          )}

          {/* Quick Specs Row */}
          <div className="flex gap-4 px-4 pb-3 border-b border-[var(--ios-separator)]/30">
            {item.year && <div className="flex items-center gap-1.5 text-[12px] text-[var(--ios-text-secondary)]"><CalendarLucideIcon className="w-3.5 h-3.5" />{item.year}</div>}
            {itemType === 'boats' && (item.length || item.length_m) && <div className="flex items-center gap-1.5 text-[12px] text-[var(--ios-text-secondary)]"><RulerLucideIcon className="w-3.5 h-3.5" />{item.length_m || item.length}m</div>}
            {item.condition && <div className="flex items-center gap-1.5 text-[12px] text-[var(--ios-text-secondary)]"><GaugeLucideIcon className="w-3.5 h-3.5" />{item.condition}</div>}
            {item.hours_used !== undefined && item.hours_used !== null && <div className="flex items-center gap-1.5 text-[12px] text-[var(--ios-text-secondary)]"><ClockLucideIcon className="w-3.5 h-3.5" />{item.hours_used}h</div>}
            {item.horsepower && <div className="flex items-center gap-1.5 text-[12px] text-[var(--ios-text-secondary)]"><ZapLucideIcon className="w-3.5 h-3.5" />{item.horsepower}HP</div>}
          </div>
        </div>

        {/* Contact Row */}
        <div className="ios-card mb-3">
          <div className="flex gap-1.5 p-2">
            {item.dealer_phone && (
              <a href={`tel:${item.dealer_phone}`} className="flex-1 flex items-center justify-center gap-1.5 py-2 rounded-lg bg-[var(--ios-blue)] text-white text-[13px] font-semibold active:opacity-80">
                <PhoneLucideIcon className="w-4 h-4" /> Chiama
              </a>
            )}
            {item.dealer_phone && (
              <a href={`https://wa.me/${item.dealer_phone.replace(/\D/g, '').startsWith('39') ? item.dealer_phone.replace(/\D/g, '') : '39' + item.dealer_phone.replace(/\D/g, '')}`} target="_blank" rel="noopener noreferrer" className="flex-1 flex items-center justify-center gap-1.5 py-2 rounded-lg bg-[#25D366] text-white text-[13px] font-semibold active:opacity-80">
                <MessageCircleLucideIcon className="w-4 h-4" /> WhatsApp
              </a>
            )}
            {item.dealer_email && (
              <a href={`mailto:${item.dealer_email}`} className="flex-1 flex items-center justify-center gap-1.5 py-2 rounded-lg bg-[var(--ios-gray-25)] text-[var(--ios-blue)] text-[13px] font-semibold active:opacity-80">
                <MailLucideIcon className="w-4 h-4" /> Email
              </a>
            )}
          </div>
        </div>

        {/* Description */}
        {item.description && (
          <div className="ios-card mb-3" id="section-info">
            <div className="ios-list-row">
              <FileLucideIcon className="w-4 h-4 text-[var(--ios-text-tertiary)] mr-3" />
              <span className="text-[13px] font-semibold text-[var(--ios-text-primary)]">Descrizione</span>
            </div>
            <div className="px-4 pb-3">
              <ItemDescription text={item.description} className="text-[13px] text-[var(--ios-text-secondary)]" />
            </div>
          </div>
        )}

        {/* Section: Dettagli Tecnici */}
        <div className="ios-card mb-3" id="section-specs">
          <div className="ios-list-row">
            <TagLucideIcon className="w-4 h-4 text-[var(--ios-text-tertiary)] mr-3" />
            <span className="text-[13px] font-semibold text-[var(--ios-text-primary)]">Dettagli Tecnici</span>
          </div>
          <div className="px-4 py-2 space-y-0.5">
            {item.year && <SpecItem label="Anno" value={String(item.year)} />}
            {item.engine_type && <SpecItem label="Motore" value={`${item.engine_type}${item.engine_power ? ` ${item.engine_power}CV` : ''}`} />}
            {item.fuel_type && <SpecItem label="Carburante" value={item.fuel_type} />}
            {item.condition && <SpecItem label="Condizione" value={item.condition} />}
            {itemType === 'boats' && item.length && <SpecItem label="Lunghezza" value={`${item.length} m`} />}
            {itemType === 'boats' && item.width && <SpecItem label="Larghezza" value={`${item.width} m`} />}
            {itemType !== 'boats' && item.hours_used !== undefined && item.hours_used !== null && <SpecItem label="Ore" value={`${item.hours_used}h`} />}
            {item.horsepower && <SpecItem label="Potenza" value={`${item.horsepower} HP`} />}
            {item.cylinders && <SpecItem label="Cilindri" value={String(item.cylinders)} />}
            {item.displacement && <SpecItem label="Cilindrata" value={`${item.displacement} cc`} />}
            {item.weight && <SpecItem label="Peso" value={`${item.weight} kg`} />}
          </div>
        </div>

        {/* Section: Barca Dati (only boats) */}
        {itemType === 'boats' && (
          <div className="ios-card mb-3" id="section-motors">
            <div className="ios-list-row">
              <ShipIcon className="w-4 h-4 text-[var(--ios-text-tertiary)] mr-3" />
              <span className="text-[13px] font-semibold text-[var(--ios-text-primary)]">Dati Barca</span>
            </div>
            <div className="px-4 py-2 space-y-0.5">
              {(item.length_m || item.length) && <SpecItem label="Lunghezza" value={`${item.length_m || item.length}m${item.length_ft ? ` (${item.length_ft}ft)` : ''}`} />}
              {(item.beam_m || item.width) && <SpecItem label="Larghezza" value={`${item.beam_m || item.width}m${item.beam_ft ? ` (${item.beam_ft}ft)` : ''}`} />}
              {item.draft_m && <SpecItem label="Pescaggio" value={`${item.draft_m}m`} />}
              {item.cabins_count && <SpecItem label="Cabine" value={String(item.cabins_count)} />}
              {item.bathrooms_count && <SpecItem label="Bagni" value={String(item.bathrooms_count)} />}
              {item.guests_max && <SpecItem label="Ospiti" value={String(item.guests_max)} />}
              {item.boat_type && <SpecItem label="Tipo" value={item.boat_type} />}
              {item.boat_type_code && <SpecItem label="Tipologia" value={BOAT_TYPES.find(t => t.code === item.boat_type_code)?.nome || item.boat_type_code} />}
              {item.hull_material && <SpecItem label="Scafo" value={item.hull_material} />}
              {item.cabin_layout && <SpecItem label="Layout" value={item.cabin_layout} />}
            </div>
          </div>
        )}

        {/* Section: Motori (only boats) */}
        {itemType === 'boats' && (item.engines_brand || item.engines_count || item.engines_power_hp || item.engines_hours) && (
          <div className="ios-card mb-3">
            <div className="ios-list-row">
              <SettingsLucideIcon className="w-4 h-4 text-[var(--ios-text-tertiary)] mr-3" />
              <span className="text-[13px] font-semibold text-[var(--ios-text-primary)]">Motori</span>
            </div>
            <div className="px-4 py-2 space-y-0.5">
              {item.engines_brand && <SpecItem label="Motore" value={`${item.engines_brand} ${item.engines_model || ''}`} />}
              {item.engines_count && <SpecItem label="N. Motori" value={String(item.engines_count)} />}
              {item.engines_power_hp && <SpecItem label="Potenza" value={`${item.engines_power_hp} HP`} />}
              {item.engines_hours && <SpecItem label="Ore Motore" value={`${item.engines_hours}h`} />}
              {item.transmission_type && <SpecItem label="Trasmissione" value={item.transmission_type} />}
              {item.max_speed_knots && <SpecItem label="Vel. Max" value={`${item.max_speed_knots} nodi`} />}
              {item.cruising_speed_knots && <SpecItem label="Crociera" value={`${item.cruising_speed_knots} nodi`} />}
              {item.range_nm && <SpecItem label="Autonomia" value={`${item.range_nm} nm`} />}
            </div>
          </div>
        )}

        {/* Section: Impianti (only boats) */}
        {itemType === 'boats' && (item.fuel_capacity_liters || item.water_capacity_liters || item.generator_brand || item.voltage_config || item.water_maker) && (
          <div className="ios-card mb-3" id="section-tech">
            <div className="ios-list-row">
              <FuelLucideIcon className="w-4 h-4 text-[var(--ios-text-tertiary)] mr-3" />
              <span className="text-[13px] font-semibold text-[var(--ios-text-primary)]">Impianti</span>
            </div>
            <div className="px-4 py-2 space-y-0.5">
              {item.fuel_capacity_liters && <SpecItem label="Serbatoio" value={`${item.fuel_capacity_liters}L`} />}
              {item.water_capacity_liters && <SpecItem label="Acqua" value={`${item.water_capacity_liters}L`} />}
              {item.generator_brand && <SpecItem label="Generatore" value={`${item.generator_brand} ${item.generator_power_kw || ''}kW`} />}
              {item.voltage_config && <SpecItem label="Voltaggio" value={item.voltage_config} />}
              {item.water_maker && <SpecItem label="Dissalatore" value="✓" />}
              {item.autoclave && <SpecItem label="Autoclave" value="✓" />}
              {item.air_conditioning && <SpecItem label="Aria Condiz." value="✓" />}
              {item.heating && <SpecItem label="Riscaldamento" value="✓" />}
              {item.batteries_count && <SpecItem label="Batterie" value={`${item.batteries_count}x${item.batteries_capacity_ah ? ` ${item.batteries_capacity_ah}Ah` : ''}`} />}
              {item.inverter && <SpecItem label="Inverter" value={item.inverter_power_watts ? `${item.inverter_power_watts}W` : '✓'} />}
            </div>
          </div>
        )}

        {/* Section: Esterni (only boats) */}
        {itemType === 'boats' && (item.teak_deck || item.flybridge || item.bimini_type || item.stern_thruster || item.stabilizing_fins) && (
          <div className="ios-card mb-3">
            <div className="ios-list-row">
              <SunLucideIcon className="w-4 h-4 text-[var(--ios-text-tertiary)] mr-3" />
              <span className="text-[13px] font-semibold text-[var(--ios-text-primary)]">Esterni</span>
            </div>
            <div className="px-4 py-2 space-y-0.5">
              {item.teak_deck && <SpecItem label="Ponte Teak" value="✓" />}
              {item.flybridge && <SpecItem label="Flybridge" value="✓" />}
              {item.flybridge_features && <SpecItem label="Flybridge" value={item.flybridge_features} />}
              {item.bimini_type && <SpecItem label="Bimini" value={item.bimini_type} />}
              {item.stern_thruster && <SpecItem label="Elica Prua" value={`✓${item.stern_thruster_power_hp ? ` (${item.stern_thruster_power_hp}HP)` : ''}`} />}
              {item.stabilizing_fins && <SpecItem label="Stabilizzatori" value={`✓${item.fins_zero_speed ? ' (Zero Speed)' : ''}`} />}
              {item.deck_material && <SpecItem label="Ponte" value={item.deck_material} />}
              {item.superstructure_material && <SpecItem label="Soprastruttura" value={item.superstructure_material} />}
              {item.underwater_lights && <SpecItem label="Luci Subacquee" value="✓" />}
              {item.cctv_cameras_count && <SpecItem label="CCTV" value={`${item.cctv_cameras_count} cam`} />}
              {item.life_raft_capacity && <SpecItem label="Zattera" value={`${item.life_raft_capacity} pax`} />}
            </div>
          </div>
        )}

        {/* Section: Elettronica (only boats) */}
        {itemType === 'boats' && (item.radar_model || item.autopilot_model || item.ais_system || item.plotter_screen_inches) && (
          <div className="ios-card mb-3">
            <div className="ios-list-row">
              <RadioLucideIcon className="w-4 h-4 text-[var(--ios-text-tertiary)] mr-3" />
              <span className="text-[13px] font-semibold text-[var(--ios-text-primary)]">Elettronica</span>
            </div>
            <div className="px-4 py-2 space-y-0.5">
              {item.radar_model && <SpecItem label="Radar" value={item.radar_model} />}
              {item.autopilot_model && <SpecItem label="Pilota Auto." value={item.autopilot_model} />}
              {item.ais_system && <SpecItem label="AIS" value="✓" />}
              {item.plotter_screen_inches && <SpecItem label="Plotter" value={`${item.plotter_screen_inches}"`} />}
              {item.gps_model && <SpecItem label="GPS" value={item.gps_model} />}
              {item.vhf_model && <SpecItem label="VHF" value={item.vhf_model} />}
            </div>
          </div>
        )}

        {/* Section: Accessori Details */}
        {itemType === 'accessories' && (
          <div className="ios-card mb-3" id="section-accessories">
            <div className="ios-list-row">
              <PackageLucideIcon className="w-4 h-4 text-[var(--ios-text-tertiary)] mr-3" />
              <span className="text-[13px] font-semibold text-[var(--ios-text-primary)]">Dettagli</span>
            </div>
            <div className="px-4 py-2 space-y-0.5">
              {item.brand && <SpecItem label="Marca" value={item.brand} />}
              {item.model && <SpecItem label="Modello" value={item.model} />}
              {item.year && <SpecItem label="Anno" value={String(item.year)} />}
              {item.category && <SpecItem label="Categoria" value={item.category} />}
              {item.compatibility && <SpecItem label="Compatibilità" value={item.compatibility} />}
              {item.condition && <SpecItem label="Condizione" value={item.condition} />}
              {item.usage_hours !== undefined && item.usage_hours !== null && <SpecItem label="Ore Uso" value={`${item.usage_hours}h`} />}
            </div>
          </div>
        )}

        {/* Optional Features (boats new + accessories) */}
        {item?.optional_features && item.optional_features.length > 0 && (
          <div className="ios-card mb-3" id="section-optionals">
            <div className="ios-list-row">
              <StarLucideIcon className="w-4 h-4 text-[var(--ios-text-tertiary)] mr-3" />
              <span className="text-[13px] font-semibold text-[var(--ios-text-primary)]">Optionals ({item.optional_features.length})</span>
            </div>
            <div className="px-4 py-2">
              <div className="flex flex-wrap gap-1.5">
                {item.optional_features.filter((o: any) => o.is_premium).map((opt: any, idx: number) => (
                  <span key={`prem-${idx}`} className="px-2 py-0.5 bg-purple-100 text-purple-800 rounded text-[10px] font-medium">
                    {opt.name}{opt.added_price > 0 && <span className="ml-0.5 text-purple-600">+€{opt.added_price.toLocaleString('it-IT')}</span>}
                  </span>
                ))}
                {item.optional_features.filter((o: any) => !o.is_premium).map((opt: any, idx: number) => (
                  <span key={`opt-${idx}`} className="px-2 py-0.5 bg-gray-100 text-gray-700 rounded text-[10px] font-medium">
                    {opt.name}{opt.added_price > 0 && <span className="ml-0.5 text-gray-500">+€{opt.added_price.toLocaleString('it-IT')}</span>}
                  </span>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Technical Specs by Type - for boats with boat_type_code */}
        {itemType === 'boats' && item.boat_type_code && item.technical_specs && (() => {
          const specs = getTechnicalSpecs(item.technical_specs, item.boat_type_code);
          const boatTypeInfo = BOAT_TYPES.find(t => t.code === item.boat_type_code);
          if (!specs || specs.length === 0) return null;
          return (
            <div className="ios-card mb-3" id="section-techdetails">
              <div className="ios-list-row">
                <FileLucideIcon className="w-4 h-4 text-[var(--ios-text-tertiary)] mr-3" />
                <span className="text-[13px] font-semibold text-[var(--ios-text-primary)]">{boatTypeInfo?.nome || item.boat_type_code}</span>
              </div>
              <div className="px-4 py-2 space-y-0.5">
                {specs.map((spec: any, idx: number) => <SpecItem key={idx} label={spec.label} value={String(spec.value)} />)}
              </div>
            </div>
          );
        })()}

        {/* Interior & Comfort (boats) */}
        {itemType === 'boats' && (item.interior_style || item.flooring_type || item.galley_brand || item.ice_maker || item.grill_type || item.tv_count || item.audio_system) && (
          <div className="ios-card mb-3">
            <div className="ios-list-row">
              <HomeLucideIcon className="w-4 h-4 text-[var(--ios-text-tertiary)] mr-3" />
              <span className="text-[13px] font-semibold text-[var(--ios-text-primary)]">Interni & Comfort</span>
            </div>
            <div className="px-4 py-2 space-y-0.5">
              {item.interior_style && <SpecItem label="Stile Interno" value={item.interior_style} />}
              {item.flooring_type && <SpecItem label="Pavimento" value={item.flooring_type} />}
              {item.galley_brand && <SpecItem label="Cucina" value={item.galley_brand} />}
              {item.ice_maker && <SpecItem label="Ice Maker" value={`✓${item.ice_maker_location ? ` (${item.ice_maker_location})` : ''}`} />}
              {item.grill_type && <SpecItem label="Grill" value={`${item.grill_type}${item.grill_location ? ` (${item.grill_location})` : ''}`} />}
              {item.tv_count && <SpecItem label="TV" value={String(item.tv_count)} />}
              {item.audio_system && <SpecItem label="Audio" value={item.audio_system} />}
              {item.internet_package && <SpecItem label="Internet" value={item.internet_package} />}
            </div>
          </div>
        )}

        {/* Interior Details (boats) */}
        {itemType === 'boats' && (item.upholstery_material || item.teak_caulking_color || item.canopy_carbon_poles) && (
          <div className="ios-card mb-3">
            <div className="ios-list-row">
              <ArmchairLucideIcon className="w-4 h-4 text-[var(--ios-text-tertiary)] mr-3" />
              <span className="text-[13px] font-semibold text-[var(--ios-text-primary)]">Dettagli Interni</span>
            </div>
            <div className="px-4 py-2 space-y-0.5">
              {item.upholstery_material && <SpecItem label="Tappezzeria" value={item.upholstery_material} />}
              {item.teak_caulking_color && <SpecItem label="Teak Color" value={item.teak_caulking_color} />}
              {item.canopy_carbon_poles && <SpecItem label="Carbon Poles" value="✓" />}
              {item.propeller_type && <SpecItem label="Elica" value={item.propeller_type} />}
            </div>
          </div>
        )}

        {/* Navigation */}
        {itemType === 'boats' && (item.navigation_package || item.sounder_model) && (
          <div className="ios-card mb-3">
            <div className="ios-list-row">
              <CompassLucideIcon className="w-4 h-4 text-[var(--ios-text-tertiary)] mr-3" />
              <span className="text-[13px] font-semibold text-[var(--ios-text-primary)]">Navigazione</span>
            </div>
            <div className="px-4 py-2 space-y-0.5">
              {item.navigation_package && <SpecItem label="Pacchetto Nav." value={item.navigation_package} />}
              {item.sounder_model && <SpecItem label="Ecoscandaglio" value={item.sounder_model} />}
            </div>
          </div>
        )}

        {/* Entertainment */}
        {itemType === 'boats' && (item.sirius_system || item.entertainment_package) && (
          <div className="ios-card mb-3">
            <div className="ios-list-row">
              <TvLucideIcon className="w-4 h-4 text-[var(--ios-text-tertiary)] mr-3" />
              <span className="text-[13px] font-semibold text-[var(--ios-text-primary)]">Intrattenimento</span>
            </div>
            <div className="px-4 py-2 space-y-0.5">
              {item.sirius_system && <SpecItem label="Sirius" value="✓" />}
              {item.waste_mincer && <SpecItem label="Trash" value="✓" />}
            </div>
          </div>
        )}

        {/* Accessory specific features */}
        {itemType === 'accessories' && item.optional_features && item.optional_features.length > 0 && (
          <div className="ios-card mb-3">
            <div className="ios-list-row">
              <StarLucideIcon className="w-4 h-4 text-[var(--ios-text-tertiary)] mr-3" />
              <span className="text-[13px] font-semibold text-[var(--ios-text-primary)]">Caratteristiche</span>
            </div>
            <div className="px-4 py-2 space-y-0.5">
              {item.optional_features.map((opt: any, idx: number) => (
                <SpecItem key={idx} label={opt.name} value={opt.value || (opt.added_price > 0 ? `+€${opt.added_price.toLocaleString('it-IT')}` : '✓')} />
              ))}
            </div>
          </div>
        )}

        {/* SEO FAQ - only for public users */}
        {!isAuthenticated && (
          <SEOFaqSection 
            faq={itemType === 'boat' ? boatFAQs : itemType === 'motor' ? motorFAQs : accessoryFAQs} 
            title={`Domande su questa ${config.singular.toLowerCase()}`} 
            className="mt-2"
          />
        )}
      </div>

      {/* Trade Modal - iOS Sheet */}
      <IOSheet
        isOpen={showTradeModal}
        onClose={() => setShowTradeModal(false)}
        title="Proponi Trattativa"
        snapPoints={[0.5, 0.9]}
      >
        <div className="p-4">
          <form onSubmit={handleTradeSubmit}>
            <div className="mb-4">
              <label className="block text-sm font-medium mb-1">Tua offerta (€)</label>
              <input
                type="number"
                value={tradePrice}
                onChange={(e) => setTradePrice(e.target.value)}
                className="input-field"
                required
              />
            </div>
            
            <div className="mb-4">
              <label className="block text-sm font-medium mb-1">Messaggio</label>
              <textarea
                value={tradeMessage}
                onChange={(e) => setTradeMessage(e.target.value)}
                className="input-field"
                rows={3}
                placeholder="Scrivi un messaggio al venditore..."
              />
            </div>
            
            <div className="flex gap-3">
              <button
                type="button"
                onClick={() => setShowTradeModal(false)}
                className="flex-1 btn-secondary"
              >
                Annulla
              </button>
              <button
                type="submit"
                disabled={tradeMutation.isLoading}
                className="flex-1 btn-primary"
              >
                {tradeMutation.isLoading ? 'Invio...' : 'Invia'}
              </button>
            </div>
          </form>
        </div>
      </IOSheet>

      {/* Share Modal - iOS Sheet */}
      <IOSheet
        isOpen={showShareModal}
        onClose={() => setShowShareModal(false)}
        title="Condividi"
        snapPoints={[0.5, 0.9]}
      >
        <div className="p-4">
          {!generatedLink ? (
            <form onSubmit={(e) => {
              e.preventDefault();
              shareMutation.mutate({ expires_hours: shareHours });
            }}>
              <div className="mb-4">
                <label className="block text-sm font-medium mb-1">Valido per</label>
                <select
                  value={shareHours}
                  onChange={(e) => setShareHours(parseInt(e.target.value))}
                  className="input-field"
                >
                  <option value={24}>24 ore</option>
                  <option value={48}>48 ore</option>
                  <option value={72}>72 ore</option>
                  <option value={168}>1 settimana</option>
                </select>
              </div>
              
              <button
                type="submit"
                disabled={shareMutation.isLoading}
                className="w-full btn-primary"
              >
                {shareMutation.isLoading ? 'Creazione...' : 'Crea Link'}
              </button>
            </form>
          ) : (
            <div className="space-y-4">
              <div className="bg-green-50 border border-green-200 rounded-lg p-3">
                <p className="text-sm text-green-800">✅ Link creato!</p>
                <p className="text-xs text-green-600">Valido per {shareHours} ore</p>
              </div>
              
              <input
                type="text"
                value={generatedLink}
                readOnly
                className="input-field text-sm"
                onClick={(e) => (e.target as HTMLInputElement).select()}
              />
              
              <div className="flex gap-3">
                <button
                  onClick={shareWhatsApp}
                  className="flex-1 bg-green-500 text-white py-3 rounded-lg font-medium hover:bg-green-600 flex items-center justify-center gap-2"
                >
                  <ShareLucideIcon className="w-4 h-4" />
                  WhatsApp
                </button>
                <button
                  onClick={() => {
                    navigator.clipboard.writeText(generatedLink);
                    toast.success('Copiato!');
                  }}
                  className="flex-1 btn-secondary"
                >
                  Copia Link
                </button>
              </div>
              
              <button
                onClick={() => {
                  setGeneratedLink('');
                  setShareData(null);
                }}
                className="w-full text-sm text-gray-500 hover:text-gray-700"
              >
                Crea nuovo link
              </button>
            </div>
          )}
        </div>
      </IOSheet>
      
      {/* Mark Sold Confirmation Modal - Mobile */}
      <IOSheet
        isOpen={showMarkSoldModal}
        onClose={() => setShowMarkSoldModal(false)}
        title="Conferma Vendita"
        snapPoints={[0.4]}
      >
        <div className="p-4 space-y-4">
          <div className="text-center">
            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <CheckCircleLucideIcon className="w-8 h-8 text-green-600" />
            </div>
            <h3 className="text-lg font-semibold text-gray-900 mb-2">
              Segna come Venduto
            </h3>
            <p className="text-gray-600">
              Stai per segnare questo {config.singular.toLowerCase()} come <strong>venduto</strong>.
              Questa azione può essere annullata solo da un amministratore.
            </p>
          </div>
          
          <div className="flex gap-3 pt-4">
            <button
              onClick={() => setShowMarkSoldModal(false)}
              className="flex-1 btn-secondary"
              disabled={markSoldMutation.isLoading}
            >
              Annulla
            </button>
            <button
              onClick={() => markSoldMutation.mutate()}
              disabled={markSoldMutation.isLoading}
              className="flex-1 btn-primary bg-green-600 hover:bg-green-700 border-green-600"
            >
              {markSoldMutation.isLoading ? 'Conferma...' : 'Conferma Vendita'}
            </button>
          </div>
        </div>
      </IOSheet>

    </div>
  );
}
